import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import type {
  Meeting,
  MeetingMinutesActionItem,
  MeetingMinutesData,
  MeetingMinutesTopic,
  DirectoryUserRow,
  TranscriptSegment,
} from '@ai-meeting/shared'
import { EMPTY_MEETING_MINUTES, MINUTES_EMPTY_TRANSCRIPT } from '@ai-meeting/shared'
import { getLanguage } from '@/constants/languages'
import {
  clampToMaxLength,
  MEETING_TITLE_MAX_LENGTH,
  MINUTES_ACTION_CONTENT_MAX_LENGTH,
  MINUTES_ASSIGNEE_MAX_LENGTH,
  MINUTES_CONCLUSION_ITEM_MAX_LENGTH,
  MINUTES_DEADLINE_MAX_LENGTH,
  MINUTES_DISCUSSION_ITEM_MAX_LENGTH,
  MINUTES_PURPOSE_ITEM_MAX_LENGTH,
  MINUTES_TOPIC_TITLE_MAX_LENGTH,
} from '@/constants/meeting'
import { fmt, type UiStrings } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import SidebarToggle from '@/components/SidebarToggle'
import DateInput from '@/components/DateInput'
import TenantUserSelect from '@/components/TenantUserSelect'
import BacklogConnectionPanel, {
  BacklogConnectedBadge,
} from '@/components/BacklogConnectionPanel'
import BacklogMemberSelect from './components/BacklogMemberSelect'
import {
  AssigneeIdentity,
  BacklogActionCell,
  BacklogIssueTypeCell,
  BacklogRowStatusCell,
} from './components/BacklogRowCells'
import BacklogConfirmPanel from './components/BacklogConfirmPanel'
import BacklogSyncSummaryBar from './components/BacklogSyncSummaryBar'
import { isAlreadyCreated, needsConfirmation, ticketFromRow } from './utils/backlogConfirmDraft'
import { clearInvalidBacklogAssignees } from './utils/clearInvalidBacklogAssignees'
import { tallySync } from './utils/backlogTally'
import ProjectSelect from '@/components/ProjectSelect'
import MoveProjectDialog from './components/MoveProjectDialog'
import { useEditableFieldFocused } from '@/hooks/useEditableFieldFocused'
import { isMinutesSaveDisabled } from './minutesSaveState'
import { logActivity } from '@/services/activity'
import { useBacklogConnection } from '@/hooks/useBacklogConnection'
import { useBacklogSync, type BacklogSyncState } from '@/hooks/useBacklogSync'
import { useTenantDirectory } from '@/hooks/useTenantDirectory'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  UploadIcon,
  XMarkIcon,
} from '@/components/Icons'
import { limitToSpeakerRuns } from './transcriptPreviewWindow'
import { pairStoredTranscriptTurns } from './storedTranscriptTurns'
import { isTranscriptPollComplete } from './transcriptPollState'
import {
  fetchMeetingAudioStatus,
  fetchMeetingById,
  requestMeetingMinutes,
  requestMeetingMinutesFromAudio,
  shareMeetingMinutesByEmail,
  updateMeeting,
} from '@/services/meetings'
import { ApiError } from '@/services/api'
import { getCurrentUserId } from '@/services/session'
import { translateMeetingMinutes } from '@/services/ai'
import { useStore } from '@/store/useStore'
import { runInternalMinutesJobInBackground } from '@/pages/Meetings/utils/internalMeetingPersistence'
import { segmentsToPlainTranscript } from '@/utils/segmentsToPlainTranscript'
import {
  downloadBlob,
  meetingMinutesToDocxBlob,
  type MeetingMinutesLocale,
} from '@/utils/meetingMinutesToDocx'
import { sanitizeFilename } from '@/utils/filename'
import type { LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'
import { getSpeakerColor } from '@/utils/speaker-colors'
import { formatElapsedRange } from '@/utils/formatElapsedTime'
import { getSttProviderLabel } from '@/utils/sttProviderLabel'
import { formatDate, formatTime } from '@/utils/formatDateTime'
import MeetingAudioPlayer from './components/MeetingAudioPlayer'
import MeetingFeedbackModal from './components/MeetingFeedbackModal'
import ShareMinutesEmailModal, {
  type ShareMinutesEmailLocale,
  type ShareMinutesEmailPayload,
} from './components/ShareMinutesEmailModal'
import ShareMinutesEmailSentModal from './components/ShareMinutesEmailSentModal'

const SUMMARY_POLL_INTERVAL_MS = 2000
const SUMMARY_POLL_TIMEOUT_MS = 90_000
/** Internal meetings queue whole-file audio minutes — upstream can run ~30m. */
const INTERNAL_AUDIO_MINUTES_POLL_TIMEOUT_MS = 31 * 60_000
const AUDIO_STATUS_POLL_INTERVAL_MS = 5000
// Comfortably past the backend's own 10-minute stale-upload reconciliation
// sweep (see MeetingsService.reconcileStalePendingAudio), so by the time this
// gives up the backend has virtually always already resolved PENDING to a
// terminal state.
const AUDIO_STATUS_POLL_TIMEOUT_MS = 12 * 60_000
const MEETING_DETAIL_FALLBACK_BACK_PATH = '/history'

const EXPORT_LOCALE_LABELS: Record<MeetingMinutesLocale, string> = {
  ja: '日本語',
  vi: 'Tiếng Việt',
  en: 'English',
}

function shareMinutesDefaultLocale(targetLang: string): ShareMinutesEmailLocale {
  return targetLang === 'ja' ? 'ja' : 'vi'
}

type MeetingDetailBackPath = '/home' | '/history'
type MeetingDetailLocationState = {
  from?: MeetingDetailBackPath
}

function getMeetingDetailBackPath(state: unknown): MeetingDetailBackPath {
  const from = (state as MeetingDetailLocationState | null)?.from
  return from === '/home' || from === '/history' ? from : MEETING_DETAIL_FALLBACK_BACK_PATH
}

function formatDurationLabel(seconds: number, t: UiStrings): string {
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `${minutes} ${minutes === 1 ? t.minute : t.minutes}`
}

function getLanguageLabel(language: string): string {
  return language.slice(0, 2).toUpperCase()
}

/**
 * Brings a minutes field into view and puts the caret in it.
 *
 * `nearest` remains the default: it moves each scrollport the least it can
 * and avoids dragging the document below the app's full-height shell
 * (BRAI-678 review). A caller may explicitly request `center` when the new
 * row needs surrounding context — notably a topic appended near the bottom
 * of a long minutes page (#595 follow-up).
 */
function focusMinutesField(field: string, block: ScrollLogicalPosition = 'nearest') {
  const node = document.querySelector<HTMLElement>(`[data-minutes-field="${field}"]`)
  node?.scrollIntoView?.({ block })
  node?.focus({ preventScroll: true })
}

/** Re-apply `done` flags by index after translate (upstream may drop the field). */
function preserveActionItemDone(
  source: MeetingMinutesData,
  translated: MeetingMinutesData
): MeetingMinutesData {
  return {
    ...translated,
    action_items: translated.action_items.map((item, index) => ({
      ...item,
      done: source.action_items[index]?.done === true,
    })),
  }
}

/**
 * First max-length violation in the minutes draft, in document order.
 * `topic.overview` is intentionally excluded — it is not in the business spec.
 */
function findMinutesLengthViolation(
  title: string,
  minutes: MeetingMinutesData,
  messages: { meetingNameTooLong: string; fieldTooLong: (max: number) => string }
): { field: string; message: string } | null {
  if (title.trim().length > MEETING_TITLE_MAX_LENGTH) {
    return { field: 'meeting-title', message: messages.meetingNameTooLong }
  }

  for (let index = 0; index < minutes.purpose.length; index++) {
    if (minutes.purpose[index].length > MINUTES_PURPOSE_ITEM_MAX_LENGTH) {
      return {
        field: `purpose-${index}`,
        message: messages.fieldTooLong(MINUTES_PURPOSE_ITEM_MAX_LENGTH),
      }
    }
  }

  for (let topicIndex = 0; topicIndex < minutes.topics.length; topicIndex++) {
    const topic = minutes.topics[topicIndex]
    if (topic.title.length > MINUTES_TOPIC_TITLE_MAX_LENGTH) {
      return {
        field: `topic-title-${topicIndex}`,
        message: messages.fieldTooLong(MINUTES_TOPIC_TITLE_MAX_LENGTH),
      }
    }
    for (let index = 0; index < topic.discussions.length; index++) {
      if (topic.discussions[index].length > MINUTES_DISCUSSION_ITEM_MAX_LENGTH) {
        return {
          field: `discussion-${topicIndex}-${index}`,
          message: messages.fieldTooLong(MINUTES_DISCUSSION_ITEM_MAX_LENGTH),
        }
      }
    }
    for (let index = 0; index < topic.conclusions.length; index++) {
      if (topic.conclusions[index].length > MINUTES_CONCLUSION_ITEM_MAX_LENGTH) {
        return {
          field: `conclusion-${topicIndex}-${index}`,
          message: messages.fieldTooLong(MINUTES_CONCLUSION_ITEM_MAX_LENGTH),
        }
      }
    }
  }

  for (let index = 0; index < minutes.action_items.length; index++) {
    const item = minutes.action_items[index]
    if (item.content.length > MINUTES_ACTION_CONTENT_MAX_LENGTH) {
      return {
        field: `action-content-${index}`,
        message: messages.fieldTooLong(MINUTES_ACTION_CONTENT_MAX_LENGTH),
      }
    }
    if (item.assignee.length > MINUTES_ASSIGNEE_MAX_LENGTH) {
      return {
        field: `action-assignee-${index}`,
        message: messages.fieldTooLong(MINUTES_ASSIGNEE_MAX_LENGTH),
      }
    }
    if (item.deadline.length > MINUTES_DEADLINE_MAX_LENGTH) {
      return {
        field: `action-deadline-${index}`,
        message: messages.fieldTooLong(MINUTES_DEADLINE_MAX_LENGTH),
      }
    }
  }

  return null
}

/**
 * Resolve the translation segments to render. Newer meetings persist a
 * dedicated `translationSegments` array straight from the socket. Older
 * meetings only have `transcript[].translation` — synthesize segments from
 * those so the detail page still renders bilingual content.
 */
function resolveTranslationSegments(meeting: Meeting): TranscriptSegment[] {
  if (meeting.translationSegments && meeting.translationSegments.length > 0) {
    return meeting.translationSegments
  }
  return meeting.transcript
    .filter((segment) => segment.translation)
    .map((segment, index) => ({
      id: segment.id ? `${segment.id}-tx` : `legacy-tx-${index}`,
      speaker: segment.speaker,
      text: segment.translation ?? '',
      timestamp: segment.timestamp,
      // Carried over, or this synthesized column would be labelled from a
      // baseline while the source column beside it used its own offsets.
      ...(typeof segment.elapsedMs === 'number' ? { elapsedMs: segment.elapsedMs } : {}),
      isFinal: segment.isFinal,
      translation_latency_ms: segment.translation_latency_ms,
    }))
}

/**
 * True when the minutes job failed for the one reason another attempt cannot
 * fix: the AI service read the recording and recognised no speech in it.
 *
 * The screen says that plainly and withholds Retry, because the generic
 * "not available yet" invites a press that spends a whole speech-to-text pass
 * reaching the same verdict (BRAI-765). Every other failure, including one
 * that recorded no reason at all, keeps the Retry it has always had.
 */
function hasNoSpeechFailure(
  meeting: Pick<Meeting, 'insightsStatus' | 'insightsFailureReason'> | null | undefined,
): boolean {
  return (
    meeting?.insightsStatus === 'FAILED' &&
    meeting.insightsFailureReason === MINUTES_EMPTY_TRANSCRIPT
  )
}

/**
 * What to put in the minutes section for a meeting whose job failed. A
 * recording with no speech in it gets said plainly: the generic "not available
 * yet, Retry to generate" is both untrue and an invitation to press a button
 * that is no longer there.
 */
function minutesFailureMessage(
  meeting: Pick<Meeting, 'insightsStatus' | 'insightsFailureReason'>,
  t: UiStrings,
): string {
  return hasNoSpeechFailure(meeting)
    ? t.meetingMinutesNoSpeech
    : t.meetingMinutesUnavailable
}

/**
 * One titled box on the meeting detail page.
 *
 * `count` renders as `· N` beside the title so a reader can size a section
 * — how many topics, how many actions — without reading it. The header carries
 * no rule under it: the sections are separate cards now, and a divider inside
 * each one made every card read as two.
 */
function DetailCard({
  title,
  count,
  children,
  action,
  titleAside,
}: {
  title: string
  /** Omitted for the sections that hold one thing rather than a list. */
  count?: number
  /** Omitted by the placeholder cards an unfinished meeting draws. */
  children?: React.ReactNode
  action?: React.ReactNode
  /**
   * Sits against the heading, left of the gap. For a control the heading
   * names — `action` is the other end of the header and would read as one
   * more button beside Push.
   */
  titleAside?: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-5 sm:pt-5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h2
            className="text-sm font-semibold text-[var(--text-primary)]"
            // Spelt out, because neither the margin nor a literal space would
            // survive: an accessible name is built by trimming each node's text
            // and joining the results, so a space at the edge of the count is
            // dropped and the heading reads “Next actions· 3”. Measured rather
            // than assumed — the test for this queries the name itself.
            aria-label={count === undefined ? undefined : `${title} · ${count}`}
          >
            {title}
            {count === undefined ? null : (
              <span className="ml-1.5 font-normal text-[var(--text-tertiary)]">· {count}</span>
            )}
          </h2>
          {titleAside}
        </div>
        {action}
      </header>
      <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">{children}</div>
    </section>
  )
}

/**
 * Save and Cancel for one card.
 *
 * Per card rather than once in the header: the header's pair wrote the whole
 * page and closed every open card with it, so saving a purpose also committed
 * a half-typed topic (BRAI-678 review). These write their own card and leave
 * the rest of the page exactly as the reader left it.
 */
function SectionEditActions({
  saving,
  disabled,
  onSave,
  onCancel,
  t,
}: {
  saving: boolean
  disabled: boolean
  onSave: () => void
  onCancel: () => void
  t: UiStrings
}) {
  return (
    <div className="mt-3 flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t.cancel}
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={disabled}
        className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
      >
        <CheckIcon className="h-3.5 w-3.5" />
        {saving ? t.saving : t.save}
      </button>
    </div>
  )
}

/**
 * The `+ Label` control behind every add affordance on this page.
 *
 * The handler is passed in rather than the list adding to itself because in
 * read-only mode the press has to open the editor first — the row it creates
 * would otherwise have nothing to be typed into.
 */
function AddRowButton({
  label,
  onClick,
  variant = 'inline',
  disabled,
}: {
  label: string
  onClick: () => void
  /**
   * `block` is the dashed full-width row under a list; `inline` is the
   * bordered chip in a card header; `link` is the bare accent text the Next
   * actions table carries under it, where the design puts the control that
   * adds a row rather than in the header beside the push.
   */
  variant?: 'inline' | 'block' | 'link'
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        variant === 'block'
          ? 'flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] text-xs font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50'
          : variant === 'link'
            ? 'inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[var(--accent)] transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50'
            : 'inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--border-color)] px-2.5 py-1 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50'
      }
    >
      <PlusIcon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

/**
 * A `<textarea>` kept exactly as tall as what it holds.
 *
 * The minutes fields open one line high, which is right for the many that
 * hold one line and wrong the moment a line wraps: the box scrolled a couple
 * of words at a time, and the only way to read a long purpose back was to
 * drag `resize-y` open by hand (BRAI-730). The height is measured from
 * `scrollHeight` on every change, so the field follows the text down as it is
 * typed and back up when it is cut.
 *
 * `resize-y` goes with it. A height the reader dragged is overwritten by
 * their next keystroke, which reads as the box fighting back.
 */
function AutoGrowTextArea({
  value,
  ...textAreaProps
}: React.ComponentProps<'textarea'> & { value: string }) {
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const field = fieldRef.current
    if (!field) return
    // Collapsed first: `scrollHeight` on a box already taller than its text
    // reports the box, so without this the field could only ever grow.
    field.style.height = 'auto'
    // Zero means nothing has been laid out — a `display: none` ancestor, or
    // an environment with no layout engine at all. Writing that back would
    // collapse the field to nothing and leave it there.
    if (field.scrollHeight > 0) field.style.height = `${field.scrollHeight}px`
  }, [value])

  return <textarea ref={fieldRef} value={value} {...textAreaProps} />
}

/**
 * `12 / 500`, on the field's own row.
 *
 * It used to be a line of its own under every field, which made a column of
 * one-line fields half again as tall as the text in it (BRAI-730). Beside the
 * field it costs no height, and it is still next to the thing it counts.
 * `tabular-nums` so it does not twitch as the number changes under the
 * reader's own typing.
 */
function FieldCounter({ length, maxLength }: { length: number; maxLength: number }) {
  return (
    <span className="shrink-0 pb-1 text-[10px] font-semibold tabular-nums text-slate-600 dark:text-[var(--text-tertiary)]">
      {length} / {maxLength}
    </span>
  )
}

/**
 * Single-line field used for short values (meeting name, topic title, table
 * cells). Read-only mode renders plain text; edit mode always renders the
 * `<input>` directly (no per-field click needed — the page-level Edit button
 * puts every field into edit mode at once). Buffers keystrokes locally and
 * only calls `onCommit` on blur (trimmed, and only if changed), matching
 * {@link EditableTextField}'s commit/cancel semantics.
 */
function EditableInlineField({
  value,
  placeholder,
  onCommit,
  readOnly,
  className,
  inputClassName,
  maxLength,
  showCounter,
  dataField,
}: {
  value: string
  placeholder?: string
  onCommit: (value: string) => void
  readOnly: boolean
  className?: string
  inputClassName?: string
  maxLength?: number
  showCounter?: boolean
  dataField?: string
}) {
  const [draftValue, setDraftValue] = useState(value)

  useEffect(() => {
    setDraftValue(value)
  }, [value])

  function commitEdit() {
    const nextValue = draftValue.trim()
    setDraftValue(nextValue)
    if (nextValue !== value.trim()) onCommit(nextValue)
  }

  function cancelEdit() {
    setDraftValue(value)
  }

  if (readOnly) {
    return (
      <span
        title={value || undefined}
        className={className ?? 'block w-full px-2 py-1 text-left text-sm'}
      >
        {value || <span className="text-[var(--text-tertiary)]">{placeholder}</span>}
      </span>
    )
  }

  return (
    <div className="flex items-end gap-2">
      <input
        type="text"
        value={draftValue}
        placeholder={placeholder}
        maxLength={maxLength}
        data-minutes-field={dataField}
        onChange={(event) => {
          const nextValue =
            maxLength === undefined
              ? event.target.value
              : clampToMaxLength(event.target.value, maxLength)
          setDraftValue(nextValue)
        }}
        onBlur={commitEdit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            cancelEdit()
          }
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
        }}
        className={
          inputClassName ??
          'min-w-0 flex-1 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--ring)]'
        }
      />
      {showCounter && maxLength !== undefined ? (
        <FieldCounter length={draftValue.length} maxLength={maxLength} />
      ) : null}
    </div>
  )
}

/**
 * Multi-line field reused for the meeting-info-adjacent overview text of each
 * topic block. Read-only mode renders plain text; edit mode always renders
 * the `<textarea>` directly (see {@link EditableInlineField} for why).
 */
function EditableTextField({
  value,
  placeholder,
  rows = 1,
  onCommit,
  readOnly,
  maxLength,
  showCounter,
  counterOnFocus,
  dataField,
}: {
  value: string
  placeholder?: string
  rows?: number
  onCommit: (value: string) => void
  readOnly: boolean
  maxLength?: number
  showCounter?: boolean
  /**
   * Keep the counter out of the layout until this field has focus.
   *
   * For the action items table, where the field shares a row with five other columns: the counter is
   * a caption under the textarea, so leaving it there permanently makes that one column ~17px taller
   * than every other and the row stops reading as a row. Opt-in rather than the default because the
   * fields that stand alone — Purpose, each topic's overview — have no neighbour to be taller than.
   */
  counterOnFocus?: boolean
  dataField?: string
}) {
  const [draftValue, setDraftValue] = useState(value)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    setDraftValue(value)
  }, [value])

  function commitEdit() {
    const nextValue = draftValue.trim()
    setDraftValue(nextValue)
    if (nextValue !== value.trim()) onCommit(nextValue)
  }

  function cancelEdit() {
    setDraftValue(value)
  }

  if (readOnly) {
    return (
      <p className="min-w-0 flex-1 whitespace-pre-wrap px-2 py-1 text-sm leading-6 text-[var(--text-secondary)]">
        {value || <span className="text-[var(--text-tertiary)]">{placeholder}</span>}
      </p>
    )
  }

  return (
    <div className="flex items-end gap-2">
      <AutoGrowTextArea
        value={draftValue}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        data-minutes-field={dataField}
        onChange={(event) => {
          const nextValue =
            maxLength === undefined
              ? event.target.value
              : clampToMaxLength(event.target.value, maxLength)
          setDraftValue(nextValue)
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          commitEdit()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            cancelEdit()
          }
        }}
        className="min-w-0 flex-1 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm leading-6 text-[var(--text-primary)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--ring)]"
      />
      {showCounter && maxLength !== undefined && (!counterOnFocus || focused) ? (
        <FieldCounter length={draftValue.length} maxLength={maxLength} />
      ) : null}
    </div>
  )
}

/**
 * Bulleted string list shared by the Purpose card and each topic's
 * discussions / conclusions.
 *
 * Read-only mode renders plain text, and — when the list is empty and
 * `onRequestAdd` is given — an `+ Add` control beside the empty line. That
 * control is the only way into a section the meeting produced nothing for:
 * before it, a reader had to know to press Edit at the top of the page first.
 *
 * Edit mode renders every item as an always-visible field. Blur commits that
 * item (trimmed), Escape reverts it, and the row's `×` removes it. Clearing
 * an item's text still removes it on commit, which is how removal worked
 * before the `×` existed.
 */
function EditableStringList({
  items,
  emptyLabel,
  addLabel,
  inlineAddLabel,
  removeAria,
  placeholder,
  onSave,
  onRequestAdd,
  highlight,
  readOnly,
  maxLength,
  showCounter,
  fieldPrefix,
}: {
  items: string[]
  emptyLabel: string
  /** Visible on the add control in both modes — not only an accessible name. */
  addLabel: string
  /**
   * Shorter wording for the read-only control, which sits next to the empty
   * line that already names the section (“No conclusions yet.” → “Add”).
   */
  inlineAddLabel?: string
  removeAria: string
  placeholder?: string
  onSave: (items: string[]) => void
  /** Opens the editor from read-only mode with one blank row already added. */
  onRequestAdd?: () => void
  highlight?: boolean
  readOnly: boolean
  maxLength?: number
  showCounter?: boolean
  fieldPrefix?: string
}) {
  const [draftItems, setDraftItems] = useState<string[]>(items)

  useEffect(() => {
    setDraftItems(items)
  }, [items])

  /** Commits `rawValue` into row `index`, dropping any row left empty. */
  function commitEdit(index: number, rawValue: string) {
    const nextItems = draftItems
      .map((item, itemIndex) => (itemIndex === index ? rawValue.trim() : item))
      .filter(Boolean)
    setDraftItems(nextItems)
    if (JSON.stringify(nextItems) !== JSON.stringify(items)) {
      onSave(nextItems)
    }
  }

  function addItem() {
    setDraftItems((current) => [...current, ''])
  }

  /**
   * Drops row `index` outright. The surviving rows are filtered too, so a
   * blank row the reader opened and left alone does not outlive the save this
   * triggers.
   */
  function removeItem(index: number) {
    const nextItems = draftItems.filter((_, itemIndex) => itemIndex !== index).filter(Boolean)
    setDraftItems(nextItems)
    if (JSON.stringify(nextItems) !== JSON.stringify(items)) {
      onSave(nextItems)
    }
  }

  const itemClassName = highlight
    ? 'rounded-md bg-[var(--bg-accent-soft,rgba(37,99,235,0.08))] px-3 py-2'
    : 'rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2'

  const displayItems = readOnly ? items : draftItems

  return (
    <div className="space-y-2">
      {displayItems.length === 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-[var(--text-secondary)]">{emptyLabel}</p>
          {readOnly && onRequestAdd ? (
            <AddRowButton label={inlineAddLabel ?? addLabel} onClick={onRequestAdd} />
          ) : null}
        </div>
      )}

      {displayItems.length > 0 && (
        <ul className="space-y-2 text-sm leading-6 text-[var(--text-secondary)]">
          {displayItems.map((item, index) => (
            <li key={`list-item-${index}`} className={`flex items-start gap-2.5 ${itemClassName}`}>
              {highlight ? (
                <CheckIcon className="mt-1.5 h-3.5 w-3.5 shrink-0 text-[var(--accent)]" />
              ) : (
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0 flex-1">
                {readOnly ? (
                  <span className="min-w-0 flex-1 break-words text-sm leading-6 text-[var(--text-primary)]">
                    {item}
                  </span>
                ) : (
                  <div className="flex items-end gap-2">
                    <AutoGrowTextArea
                      value={item}
                      rows={1}
                      placeholder={placeholder}
                      maxLength={maxLength}
                      data-minutes-field={
                        fieldPrefix === undefined ? undefined : `${fieldPrefix}-${index}`
                      }
                      onChange={(event) => {
                        const nextValue =
                          maxLength === undefined
                            ? event.target.value
                            : clampToMaxLength(event.target.value, maxLength)
                        setDraftItems((current) =>
                          current.map((draft, draftIndex) =>
                            draftIndex === index ? nextValue : draft
                          )
                        )
                      }}
                      onBlur={(event) => commitEdit(index, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault()
                          setDraftItems(items)
                        }
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault()
                          event.currentTarget.blur()
                        }
                      }}
                      className="min-h-[36px] min-w-0 flex-1 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-sm leading-6 text-[var(--text-primary)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--ring)]"
                    />
                    {showCounter && maxLength !== undefined ? (
                      <FieldCounter length={item.length} maxLength={maxLength} />
                    ) : null}
                  </div>
                )}
              </div>
              {readOnly ? null : (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  aria-label={removeAria}
                  className="mt-1 shrink-0 rounded-md p-1 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-red-500"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {readOnly ? null : <AddRowButton label={addLabel} onClick={addItem} variant="block" />}
    </div>
  )
}

/**
 * The saved transcript: one row per turn — who spoke and when, the line, and
 * its translation beside it.
 *
 * Only possible because {@link pairStoredTranscriptTurns} relates the two
 * stored arrays. The two-column view this descends from drew them
 * independently and left the matching to the reader's eye, so there was no
 * shared row for a speaker to sit on and no way to say which translation
 * belonged to which line.
 *
 * The translation shown is whatever the meeting was saved with, which for
 * anything recorded since BRAI-642 is the batch copy — the same text the live
 * screen's Enhanced mode panel showed, rather than the rougher inline one the
 * reader watched arrive. That is the whole of what "Enhanced mode" means
 * here: the better text, in this screen's own layout.
 *
 * An untranslated row says so rather than sitting blank: in a saved meeting a
 * missing translation is a fact about the recording — one-way, or internal —
 * and an empty cell reads as the page having failed to draw it.
 */
/**
 * What the Speaker column prints for a turn with no display name.
 *
 * @param speaker The stored identifier — an index for a meeting this app
 *   transcribed, or a label for one whose transcript was parsed back out of
 *   flat `Speaker N:` text.
 * @param t       Locale strings, for the word and for the unknown fallback.
 */
function speakerCellLabel(speaker: string | undefined, t: UiStrings): string {
  if (!speaker) return t.unknownSpeaker
  return /^\d+$/.test(speaker) ? `${t.speaker} ${speaker}` : speaker
}

function StoredTranscriptTable({
  rows,
  sourceLang,
  targetLang,
  t,
}: {
  /** Already paired and already capped — the card owns both, because the
      “View full transcript” control in its header turns on how much was cut. */
  rows: LiveTranscriptTurn[]
  sourceLang: string
  targetLang: string
  t: UiStrings
}) {
  const headerClass =
    'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]'

  if (rows.length === 0) {
    return <p className="text-sm text-[var(--text-secondary)]">{t.noTranscriptCaptured}</p>
  }

  return (
    <div className="overflow-hidden rounded-lg border border-blue-100 bg-white dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
      <table className="w-full table-fixed border-collapse text-sm">
        <thead>
          <tr className="border-b border-[var(--border-color)]">
            <th scope="col" className={`w-28 ${headerClass}`}>
              {t.speaker}
            </th>
            {/* The language's name, not the two-letter badge the page header
                uses: `getLanguage('')` is where “Auto-detect” comes from, and
                “AU · Input” would say nothing. */}
            <th scope="col" className={headerClass}>
              {`${getLanguage(sourceLang).name} · ${t.input}`}
            </th>
            <th scope="col" className={headerClass}>
              {`${getLanguage(targetLang).name} · ${t.output}`}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((turn, index) => (
            <tr
              key={turn.id}
              className={[
                'border-b border-[var(--border-color)] align-top last:border-0',
                // Striped by row, not by speaker: a long run from one person
                // would otherwise be one unbroken block, which is the thing
                // the stripes are there to break up.
                index % 2 === 1 ? 'bg-[var(--bg-primary)]' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <td className="px-3 py-3">
                <span
                  className="block font-semibold"
                  style={{ color: getSpeakerColor(turn.speaker ?? '—') }}
                >
                  {/* A bare number is an index and needs the word in front
                      of it; anything else is already a label and is printed
                      as it stands. An internal meeting stores its speaker as
                      the literal `Speaker 1`, and prefixing that gave
                      `Speaker Speaker 1` — two lines in a column this narrow
                      (BRAI-734). Prefixing nothing at all would have been the
                      other way to fix it, at the price of every ordinary
                      meeting reading `1` under a column headed Speaker. */}
                  {turn.speakerName ?? speakerCellLabel(turn.speaker, t)}
                </span>
                {turn.startMs === undefined ? null : (
                  <time className="mt-0.5 block text-xs tabular-nums text-[var(--text-tertiary)]">
                    {formatElapsedRange(turn.startMs, turn.endMs)}
                  </time>
                )}
              </td>
              <td className="px-3 py-3 [overflow-wrap:anywhere] text-[var(--text-primary)]">
                {turn.sourceText}
              </td>
              <td className="px-3 py-3 [overflow-wrap:anywhere] text-[var(--text-primary)]">
                {turn.batchTranslation ?? (
                  <span className="italic text-[var(--text-tertiary)]">{t.noTranslationYet}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Meeting metadata as a two-column grid — name, date range, participants,
 * translation direction, provider, creator.
 *
 * The name is the only row this screen owns; the rest describe the session
 * that produced the meeting and cannot be edited anywhere. While editing they
 * are drawn as disabled fields so the grid stays one shape, and they are
 * taken out of the tab order so a reader working through the form never lands
 * on a box that will not take typing.
 */
function MeetingInfoFields({
  meeting,
  title,
  locale,
  t,
  onSaveTitle,
  readOnly,
}: {
  meeting: Meeting
  /** Draft meeting name shown in the editable field — may differ from `meeting.title` until Save is clicked. */
  title: string
  locale: string
  t: UiStrings
  onSaveTitle: (title: string) => void
  readOnly: boolean
}) {
  const startedAt = new Date(meeting.createdAt)
  const endedAt = new Date(meeting.createdAt + meeting.durationSeconds * 1000)
  const dateRange = `${formatDate(startedAt, locale)} ${formatTime(startedAt, locale)} - ${formatTime(endedAt, locale)}`
  const sourceLanguage = getLanguage(meeting.sourceLang)
  const targetLanguage = getLanguage(meeting.targetLang)

  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: t.meetingInfoName,
      value: (
        <EditableInlineField
          value={title}
          onCommit={onSaveTitle}
          readOnly={readOnly}
          maxLength={MEETING_TITLE_MAX_LENGTH}
          showCounter
          dataField="meeting-title"
          className="block w-full min-w-0 truncate rounded-md text-left text-sm text-[var(--text-primary)]"
        />
      ),
    },
    { label: t.meetingInfoDateTime, value: dateRange },
    { label: t.meetingInfoParticipants, value: t.meetingInfoParticipantsUnavailable },
    {
      label: t.meetingInfoTranslationDirection,
      value: `${sourceLanguage.name} → ${targetLanguage.name}`,
    },
    { label: t.meetingInfoSttProvider, value: getSttProviderLabel(meeting.sttProvider, t) },
    { label: t.meetingInfoCreatedBy, value: t.meetingInfoCreatedByValue },
    { label: t.meetingInfoCreatedDate, value: formatDate(startedAt, locale) },
  ]

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {rows.map((row) => {
        const valueTitle = typeof row.value === 'string' ? row.value : undefined
        return (
          <div key={row.label} className="min-w-0">
            <dt className="text-xs font-medium text-[var(--text-secondary)]">{row.label}</dt>
            <dd className="mt-0.5 min-w-0 text-sm text-[var(--text-primary)]" title={valueTitle}>
              {typeof row.value !== 'string' ? (
                row.value
              ) : readOnly ? (
                <span className="block truncate">{row.value}</span>
              ) : (
                // Shown as a field the editor refuses rather than as text:
                // every other row in this grid is a box while editing, and a
                // bare line among them reads as a field that failed to render
                // rather than as one this screen does not own.
                <input
                  type="text"
                  value={row.value}
                  readOnly
                  disabled
                  tabIndex={-1}
                  aria-label={row.label}
                  className="w-full cursor-not-allowed truncate rounded-md border border-[var(--border-color)] bg-[var(--bg-tertiary)] px-2 py-1 text-sm text-[var(--text-secondary)]"
                />
              )}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/**
 * One numbered topic block.
 *
 * Collapsing is a reading convenience, not part of the minutes — a folded
 * topic is still saved, exported and pushed to Backlog in full — so the state
 * is local and the control is drawn only while reading. Offering it in edit
 * mode would let a reader fold away fields they had already typed into but
 * not yet committed.
 */
function EditableTopicBlock({
  topic,
  topicIndex,
  t,
  onChange,
  onRemove,
  onRequestAdd,
  readOnly,
}: {
  topic: MeetingMinutesTopic
  topicIndex: number
  t: UiStrings
  onChange: (next: MeetingMinutesTopic) => void
  onRemove: () => void
  /** Opens the editor with one blank row already in `field` (read-only only). */
  onRequestAdd?: (field: 'discussions' | 'conclusions') => void
  readOnly: boolean
}) {
  const [collapsed, setCollapsed] = useState(false)
  const bodyId = useId()
  const showBody = !readOnly || !collapsed

  return (
    <div className="mb-2.5 overflow-hidden rounded-lg border border-[var(--border-color)] last:mb-0">
      <div className="flex items-center gap-2 bg-[var(--bg-tertiary)] px-3 py-2">
        <span className="shrink-0 text-xs font-bold tabular-nums text-[var(--accent)]">
          {String(topicIndex + 1).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <EditableInlineField
            value={topic.title}
            placeholder={t.topicTitlePlaceholder}
            onCommit={(title) => onChange({ ...topic, title })}
            readOnly={readOnly}
            maxLength={MINUTES_TOPIC_TITLE_MAX_LENGTH}
            showCounter
            dataField={`topic-title-${topicIndex}`}
            className="block w-full rounded-md text-left text-sm font-semibold text-[var(--text-primary)]"
          />
        </div>
        {readOnly ? (
          <button
            type="button"
            onClick={() => setCollapsed((open) => !open)}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            aria-label={collapsed ? t.expandTopicAria : t.collapseTopicAria}
            className="shrink-0 rounded-md p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)]"
          >
            {collapsed ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronUpIcon className="h-4 w-4" />
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={onRemove}
            aria-label={t.removeTopicAria}
            className="shrink-0 rounded-md p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-primary)] hover:text-red-500"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {showBody && (
        <div id={bodyId} className="space-y-3 px-3 py-3">
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              {t.topicOverviewLabel}
            </p>
            <EditableTextField
              value={topic.overview}
              placeholder={t.topicOverviewPlaceholder}
              onCommit={(overview) => onChange({ ...topic, overview })}
              readOnly={readOnly}
            />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              {t.topicDiscussionsLabel}
            </p>
            <EditableStringList
              items={topic.discussions}
              emptyLabel={t.discussionsUnavailable}
              addLabel={t.addDiscussion}
              removeAria={t.removeDiscussionAria}
              onSave={(discussions) => onChange({ ...topic, discussions })}
              onRequestAdd={onRequestAdd && (() => onRequestAdd('discussions'))}
              readOnly={readOnly}
              maxLength={MINUTES_DISCUSSION_ITEM_MAX_LENGTH}
              showCounter
              fieldPrefix={`discussion-${topicIndex}`}
            />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              {t.topicConclusionsLabel}
            </p>
            <EditableStringList
              items={topic.conclusions}
              emptyLabel={t.conclusionsUnavailable}
              addLabel={t.addConclusion}
              inlineAddLabel={t.addShort}
              removeAria={t.removeConclusionAria}
              onSave={(conclusions) => onChange({ ...topic, conclusions })}
              onRequestAdd={onRequestAdd && (() => onRequestAdd('conclusions'))}
              readOnly={readOnly}
              highlight
              maxLength={MINUTES_CONCLUSION_ITEM_MAX_LENGTH}
              showCounter
              fieldPrefix={`conclusion-${topicIndex}`}
            />
          </div>
        </div>
      )}
    </div>
  )
}

/** Stable empty list, so the transcript pairing below is not redone every render. */
const EMPTY_SEGMENTS: TranscriptSegment[] = []

/** Stable empty list so a meeting with no minutes does not re-key the sync hook. */
const EMPTY_ACTION_ITEMS: MeetingMinutesActionItem[] = []

const ACTION_ITEMS_PREVIEW_LIMIT = 5

// `py-2` and `leading-6` rather than `py-1`: they make this button the same height as the content
// textarea's first line and the assignee select, which is what puts all three on one line. It was
// the odd one out, sitting about 6px high.
const DEADLINE_DATE_INPUT_CLASS =
  'w-full rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-2 text-sm leading-6 text-[var(--text-primary)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--ring)]'

/**
 * Puts a cell's bare content on the same line as the text inside the row's fields.
 *
 * The fields carry chrome that bare text does not: in edit mode a 24px line inside `py-2` and a 1px
 * border, in read-only the same line inside `py-1` and no border. Without mirroring that box, "01",
 * the Backlog status and the delete button sit roughly 13px above the inputs and the row reads as
 * ragged. Mirrored rather than nudged with a fixed offset because the two modes render fields at
 * different heights — a single magic number would fix one and break the other.
 */
function actionCellLineClass(readOnly: boolean): string {
  return readOnly
    ? 'flex min-h-6 items-center py-1'
    : 'flex min-h-6 items-center border border-transparent py-2'
}

function parseIsoDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!year || !month || !day) return null
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

function isoDateInputValueOrEmpty(value: string): string {
  return parseIsoDateInputValue(value) ? value.trim() : ''
}

/** Deadline cell: read-only shows legacy text as-is; edit mode uses {@link DateInput}. */
function EditableDeadlineCell({
  value,
  rowNumber,
  onChange,
  readOnly,
  dataField,
  t,
}: {
  value: string
  rowNumber: number
  onChange: (deadline: string) => void
  readOnly: boolean
  dataField: string
  t: UiStrings
}) {
  const ariaLabel = `${t.actionItemsTableDeadline} ${String(rowNumber).padStart(2, '0')}`

  if (readOnly) {
    return (
      <span
        title={value || undefined}
        className="block w-full px-2 py-1 text-left text-sm"
      >
        {/* The same `dd/mm/yyyy` the edit-mode field shows, so one row reads
            the same whichever mode it is in (BRAI-575 review). */}
        {value || <span className="text-[var(--text-tertiary)]">{t.datePlaceholder}</span>}
      </span>
    )
  }

  return (
    <DateInput
      value={isoDateInputValueOrEmpty(value)}
      onChange={onChange}
      ariaLabel={ariaLabel}
      placeholder={t.datePlaceholder}
      className={DEADLINE_DATE_INPUT_CLASS}
      dataMinutesField={dataField}
    />
  )
}

/** Editable action items table with Done/No/Content/Assignee/Deadline columns and a show-all/collapse toggle beyond {@link ACTION_ITEMS_PREVIEW_LIMIT} rows. */
function EditableActionItemsTable({
  items,
  t,
  onChange,
  readOnly,
  directory,
  backlog,
  backlogConnected = false,
}: {
  items: MeetingMinutesActionItem[]
  t: UiStrings
  onChange: (next: MeetingMinutesActionItem[]) => void
  readOnly: boolean
  directory: DirectoryUserRow[]
  /** Absent, or configured-but-unchecked, leaves the Backlog columns out entirely. */
  backlog?: BacklogSyncState
  /**
   * Whether the reader has a usable Backlog grant. Gates the project-roster
   * assignee picker: without a grant the roster cannot be read, so the tenant
   * picker stays (BRAI-537).
   */
  backlogConnected?: boolean
}) {
  const useBacklogAssignee = Boolean(backlog?.config?.assign_enabled && backlogConnected)
  const [showAll, setShowAll] = useState(false)
  const visibleItems = !readOnly || showAll ? items : items.slice(0, ACTION_ITEMS_PREVIEW_LIMIT)

  function updateItem(index: number, patch: Partial<MeetingMinutesActionItem>) {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)))
  }

  function addItem() {
    onChange([...items, { content: '', assignee: '', deadline: '', done: false }])
  }

  function removeItem(index: number) {
    onChange(items.filter((_, itemIndex) => itemIndex !== index))
  }

  if (items.length === 0) {
    return (
      <div className="space-y-3">
        {/* The read-only line names the control that fixes it; the editor's
            does not, because that control is already on screen below. */}
        <p className="text-sm text-[var(--text-secondary)]">
          {readOnly ? t.actionItemsEmptyHint : t.actionItemsEmptyShort}
        </p>
        {!readOnly && (
          <button
            type="button"
            onClick={addItem}
            aria-label={t.addActionItemAria}
            className="flex h-8 w-full items-center justify-center rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--accent)]"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    )
  }

  const cellLineClass = actionCellLineClass(readOnly)
  /**
   * Reading the row, every cell centres on one line; editing, they hang from
   * the top.
   *
   * The cells do not share a box: the three text ones render read-only as
   * `px-2 py-1` around a 20px line (28px tall), while the rest wrap theirs in
   * `cellLineClass` (`min-h-6` + `py-1`, 32px). Measured in a browser against
   * the real markup, `align-top` spread the six first lines over 22px —
   * `align-middle` puts content, deadline, Backlog and Status on exactly the
   * same axis as the row number, since each one is already centred inside its
   * own box whatever that box's height is.
   *
   * Editing keeps `align-top`: the content field drags open and the pickers
   * drop panels, and both would push everything beside them down the row.
   */
  const cellAlignClass = readOnly ? 'align-middle' : 'align-top'
  /**
   * The two Backlog columns while the minutes are being edited.
   *
   * They stay on screen, but nothing in them can be changed and everything
   * beside them can, which is the confusion the review reported. The editable
   * fields are already `--text-primary`; dimming the two that are not is what
   * makes the difference visible (BRAI-575 review).
   */
  const backlogCellClass = `px-2 py-2 ${cellAlignClass}${readOnly ? '' : ' opacity-60'}`
  // No, Content, Assignee, Deadline, plus the two Backlog columns when they
  // are shown and the delete button's column while editing. The confirmation
  // panel spans all of them, and a wrong count leaves it short of the table.
  const backlogColumnCount = 4 + (backlog?.config ? 2 : 0) + (readOnly ? 0 : 1)

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="bg-[var(--bg-tertiary)] text-left text-[11px] font-semibold text-[var(--text-secondary)]">
              <th className={backlog?.config ? 'w-20 px-2 py-2' : 'w-12 px-2 py-2'}>
                {backlog?.config ? (
                  // The cell below puts a tick box before the number, so a
                  // header sitting at the cell's left edge labels the box
                  // rather than the column. The spacer is that box's own
                  // geometry — 18px from `.checkbox-accent`, 8px from `gap-2`.
                  <span className="flex items-center gap-2">
                    <span aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                    {t.actionItemsTableNo}
                  </span>
                ) : (
                  t.actionItemsTableNo
                )}
              </th>
              <th className="px-2 py-2">{t.actionItemsTableContent}</th>
              <th className="w-44 px-2 py-2">{t.actionItemsTableAssignee}</th>
              <th className="w-36 px-2 py-2">{t.actionItemsTableDeadline}</th>
              {backlog?.config && (
                <>
                  <th className="w-36 px-2 py-2">{t.actionItemsTableIssueType}</th>
                  <th className="w-40 px-2 py-2">{t.backlogColBacklog}</th>
                  <th className="w-28 px-2 py-2">{t.backlogColStatus}</th>
                </>
              )}
              {!readOnly && <th className="w-10 px-2 py-2" aria-hidden="true" />}
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item, index) => {
              const rowNumber = String(index + 1).padStart(2, '0')
              const backlogRow = backlog?.config ? backlog.rows.get(index) : undefined
              // Open on arrival for every row the server stopped on: the row
              // carries the whole left-hand column, so there is nothing to
              // fetch and nothing to make the reviewer press first.
              // Settled by the server: the ticket exists, so the reviewer has
              // nothing left to decide about this row.
              //
              // Taking the tick box away is the whole of it. There is no second
              // rule making the cells ignore the tick, because a duplicate can
              // no longer carry one: the box is gone, and a row that was
              // unticked before the push is never sent, so it never comes back
              // as a duplicate either.
              const alreadyCreated = isAlreadyCreated(backlogRow)
              const excluded = Boolean(backlog?.skipped.has(index))
              const confirmTicket =
                needsConfirmation(backlogRow) &&
                !excluded &&
                !backlog?.collapsedConfirm.has(index)
                  ? ticketFromRow(backlogRow!)
                  : null
              return (
                <Fragment key={`action-item-${index}`}>
                <tr className="border-t border-[var(--border-color)]">
                  <td className={`px-2 py-2 text-xs text-[var(--text-tertiary)] ${cellAlignClass}`}>
                    <span className={`${cellLineClass} gap-2`}>
                      {/* The reviewer's one control over the push. Which of
                          create-or-update happens is the server's match, but
                          whether this row goes at all is theirs (BRAI-575). */}
                      {backlog?.config &&
                        (alreadyCreated ? (
                          // A spacer of exactly the box's own geometry. Dropping
                          // the element outright would pull this row's number
                          // 26px left of every other one.
                          <span aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                        ) : (
                          <input
                            type="checkbox"
                            checked={!backlog.skipped.has(index)}
                            onChange={() => backlog.toggleSkip(index)}
                            // Off while editing: the sync reads the saved
                            // minutes, so a tick made here would describe a row
                            // the server has not been told about yet.
                            disabled={!readOnly || backlog.phase !== 'idle'}
                            aria-label={fmt(t.backlogRowIncludeAria, { number: index + 1 })}
                            className="checkbox-accent"
                          />
                        ))}
                      {rowNumber}
                    </span>
                  </td>
                  <td
                    className={[
                      'px-2 py-2',
                      cellAlignClass,
                      // Only the content dims. The tick box has to stay legible
                      // — it is how the row comes back.
                      backlog?.config && excluded ? 'opacity-60' : '',
                    ].join(' ')}
                  >
                    {/*
                      One row to start with: at `leading-6` inside `py-2` and a border that is
                      42px, the same as the deadline button and the bare-content cells beside it,
                      so a row of one-line actions is one height. A longer one grows the cell
                      rather than scrolling inside it, which is the point of the field — a row
                      taller than its neighbours still beats two words of the content showing.
                    */}
                    <EditableTextField
                      value={item.content}
                      rows={1}
                      onCommit={(content) => updateItem(index, { content })}
                      readOnly={readOnly}
                      maxLength={MINUTES_ACTION_CONTENT_MAX_LENGTH}
                      showCounter
                      counterOnFocus
                      dataField={`action-content-${index}`}
                    />
                  </td>
                  <td className={`px-2 py-2 text-[var(--text-primary)] ${cellAlignClass}`}>
                    {readOnly ? (
                      // The picker's read-only form is the stored string on one
                      // line. Reviewing a push wants the address visible, since
                      // that is the half that matched a Backlog member.
                      //
                      // Wrapped in the padding the picker carries internally, so
                      // the avatar starts where the content and deadline text
                      // does instead of 8px to the left of it.
                      <span className="block px-2 py-1">
                        <AssigneeIdentity
                          value={item.assignee}
                          placeholder={t.assigneePlaceholder}
                        />
                      </span>
                    ) : useBacklogAssignee && backlog ? (
                      <BacklogMemberSelect
                        value={item.assignee_id ?? null}
                        displayName={item.assignee}
                        onChange={(member) =>
                          updateItem(index, {
                            assignee: member?.name ?? '',
                            assignee_id: member?.id ?? null,
                          })
                        }
                        members={backlog.members ?? []}
                        loading={backlog.membersLoading}
                        errorMessage={backlog.membersError}
                        onRetry={backlog.reloadMembers}
                        dataField={`action-assignee-${index}`}
                        placeholderLabel={t.assigneePlaceholder}
                        searchPlaceholder={t.assigneeSearch}
                        noResultsLabel={t.assigneeNoResults}
                        loadingLabel={t.assigneeLoading}
                        retryLabel={t.assigneeRetry}
                        ariaLabel={t.actionItemsTableAssignee}
                      />
                    ) : (
                      <TenantUserSelect
                        value={item.assignee}
                        onChange={(assignee) =>
                          updateItem(index, { assignee, assignee_id: null })
                        }
                        users={directory}
                        valueFormat="name-and-email"
                        readOnly={readOnly}
                        maxLength={MINUTES_ASSIGNEE_MAX_LENGTH}
                        dataField={`action-assignee-${index}`}
                        placeholderLabel={t.assigneePlaceholder}
                        searchPlaceholder={t.assigneeSearch}
                        tenantGroupLabel={t.assigneeTenantGroup}
                        manualLabel={t.assigneeEnterManually}
                        ariaLabel={t.actionItemsTableAssignee}
                        manualInputLabel={t.actionItemsTableAssignee}
                        backToListLabel={t.assigneeBackToList}
                        noResultsLabel={t.assigneeNoResults}
                      />
                    )}
                  </td>
                  <td className={`px-2 py-2 ${cellAlignClass}`}>
                    <EditableDeadlineCell
                      value={item.deadline}
                      rowNumber={index + 1}
                      onChange={(deadline) => updateItem(index, { deadline })}
                      readOnly={readOnly}
                      dataField={`action-deadline-${index}`}
                      t={t}
                    />
                  </td>
                  {backlog?.config && (
                    <>
                      <td className={backlogCellClass}>
                        <span className={cellLineClass}>
                          <BacklogIssueTypeCell
                            value={item.issue_type ?? ''}
                            issueTypes={backlog.issueTypes}
                            onChange={(issueType) => updateItem(index, { issue_type: issueType })}
                            readOnly={readOnly}
                            rowNumber={index + 1}
                            t={t}
                          />
                        </span>
                      </td>
                      <td className={backlogCellClass}>
                        <span className={cellLineClass}>
                          <BacklogActionCell
                            row={backlog.rows.get(index)}
                            excluded={excluded}
                            checking={backlog.phase === 'checking'}
                            t={t}
                          />
                        </span>
                      </td>
                      <td className={backlogCellClass}>
                        <span className={cellLineClass}>
                          {/* A row the server stopped on opens its panel by
                              itself — the row already carries everything the
                              panel shows. The badge folds it away again, for a
                              reviewer working through several at once. */}
                          {needsConfirmation(backlog.rows.get(index)) &&
                          !excluded ? (
                            <button
                              type="button"
                              onClick={() => backlog.toggleConfirm(index)}
                              aria-expanded={!backlog.collapsedConfirm.has(index)}
                            >
                              <BacklogRowStatusCell
                                row={backlog.rows.get(index)}
                                excluded={false}
                                t={t}
                              />
                            </button>
                          ) : (
                            <BacklogRowStatusCell
                              row={backlog.rows.get(index)}
                              excluded={excluded}
                              t={t}
                            />
                          )}
                        </span>
                        {backlog.confirmNote.get(index) === 'noop' && (
                          // Confirmed, and the server found nothing to write.
                          // Not "Updated": Backlog never saw an edit.
                          <span className="mt-1 block text-[11px] text-[var(--text-tertiary)]">
                            {t.backlogConfirmNoop}
                          </span>
                        )}
                      </td>
                    </>
                  )}
                  {!readOnly && (
                    <td className={`px-2 py-2 ${cellAlignClass}`}>
                      <span className={cellLineClass}>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          aria-label={t.removeActionItemAria}
                          className="rounded-md p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-red-500"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </span>
                    </td>
                  )}
                </tr>
                {confirmTicket && (
                  <tr
                    key={`action-item-${index}-confirm`}
                    className="border-t border-[var(--border-color)]"
                  >
                    <td colSpan={backlogColumnCount} className="px-2 pb-3 pt-1">
                      <BacklogConfirmPanel
                        ticket={confirmTicket}
                        proposed={{
                          summary: item.content,
                          assigneeId: backlog?.assigneeIds[index] ?? null,
                          dueDate: item.deadline,
                        }}
                        members={backlog?.members ?? []}
                        priorities={backlog?.priorities ?? []}
                        conflicts={backlog?.rows.get(index)?.conflicts ?? []}
                        matchScore={backlog?.rows.get(index)?.match_score ?? null}
                        matchReason={backlog?.rows.get(index)?.match_reason ?? null}
                        applying={Boolean(backlog?.confirmApplying.has(index))}
                        error={backlog?.confirmError.get(index) ?? null}
                        onApply={(payload) => void backlog?.applyConfirm(index, payload)}
                        // Unticking is the whole of Skip: the panel is only
                        // rendered for a ticked row, the tick box follows the
                        // same set, and the Status cell reads it as Skipped.
                        // Re-ticking the row is how it comes back.
                        onSkip={() => backlog?.toggleSkip(index)}
                        t={t}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {readOnly && items.length > ACTION_ITEMS_PREVIEW_LIMIT && (
        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          className="w-full rounded-md py-2 text-center text-xs font-semibold text-[var(--accent)]"
        >
          {showAll ? t.showLessActionItems : fmt(t.showAllActionItems, { count: items.length })}
        </button>
      )}

      {!readOnly && (
        <button
          type="button"
          onClick={addItem}
          aria-label={t.addActionItemAria}
          className="flex h-8 w-full items-center justify-center rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--accent)]"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

/**
 * A part of the minutes page that opens for editing by itself.
 *
 * `info` is the meeting-info card's own fields; the other three are the
 * minutes cards. Deliberately coarser than one per field: a purpose and the
 * list it belongs to open together, which is what pressing `+ Add purpose`
 * means.
 */
type MinutesEditSection = 'info' | 'purpose' | 'topics' | 'actions'

/** What Edit in the header opens. */
const ALL_EDIT_SECTIONS: readonly MinutesEditSection[] = ['info', 'purpose', 'topics', 'actions']

/** Shared so closing the editor does not allocate a set to mean "none". */
const EMPTY_EDIT_SECTIONS: ReadonlySet<MinutesEditSection> = new Set()

export default function MeetingDetailPage() {
  const { t, locale } = useUiStrings()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const backPath = getMeetingDetailBackPath(location.state)
  const [meeting, setMeeting] = useState<Meeting | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accessRemoved, setAccessRemoved] = useState(false)
  const [detailRefetching, setDetailRefetching] = useState(false)
  const [minutesSectionError, setMinutesSectionError] = useState<string | null>(null)
  const [transcriptSectionError, setTranscriptSectionError] = useState<string | null>(null)
  const [audioSectionError, setAudioSectionError] = useState<string | null>(null)
  const [isPollingTranscript, setIsPollingTranscript] = useState(false)
  const [isPollingInsights, setIsPollingInsights] = useState(false)
  /** True when minutes are missing and the user should Retry via AI generate (not soft GET). */
  const [insightsGenerateRecovery, setInsightsGenerateRecovery] = useState(false)
  const [isGeneratingMinutes, setIsGeneratingMinutes] = useState(false)
  /** Bumped by soft Retry so pending-section poll effects remount after a successful refetch that still lacks data. */
  const [transcriptPollToken, setTranscriptPollToken] = useState(0)
  const [insightsPollToken, setInsightsPollToken] = useState(0)
  const [showFullTranscript, setShowFullTranscript] = useState(false)
  /** Fresh End Meeting poll must survive pending row removal until insights land or timeout. */
  const freshInsightsPollSessionRef = useRef(false)
  /**
   * Transcript length seen by the previous poll. The poll stops once two
   * consecutive reads agree, rather than as soon as any segment exists — a
   * native transcript is projected more than once and grows between them.
   */
  const lastTranscriptCountRef = useRef(-1)

  /**
   * Fresh End Meeting keeps a pending row for this meeting id while the
   * background minutes job may still land — poll instead of showing Retry.
   * Stuck / old meetings (no pending) go straight to generate recovery.
   *
   * Since BRAI-412 the job lives on the server, so `insightsStatus` answers
   * this directly and outlives the tab: a meeting reopened hours later with a
   * job still running keeps waiting, and one whose job already gave up offers
   * Retry immediately instead of polling for 90s. The pending-row heuristic
   * stays as the fallback for rows written before that field existed.
   */
  function markInsightsPollingIfNeeded(data: Meeting, meetingIsPendingSave: boolean) {
    if (data.insights) {
      freshInsightsPollSessionRef.current = false
      setInsightsGenerateRecovery(false)
      setMinutesSectionError(null)
      return
    }
    if (useStore.getState().internalMinutesAudioQueueFailedIds[data.id]) {
      freshInsightsPollSessionRef.current = false
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(true)
      setMinutesSectionError(uiStringsRef.current.meetingMinutesUnavailable)
      return
    }
    if (data.transcript.length === 0) {
      if (data.insightsStatus === 'FAILED') {
        freshInsightsPollSessionRef.current = false
        setIsPollingInsights(false)
        setInsightsGenerateRecovery(!hasNoSpeechFailure(data))
        setMinutesSectionError(minutesFailureMessage(data, uiStringsRef.current))
        return
      }
      if (meetingIsPendingSave || data.insightsStatus === 'PENDING') {
        freshInsightsPollSessionRef.current = true
        setIsPollingInsights(true)
        setInsightsGenerateRecovery(false)
        setMinutesSectionError(null)
        return
      }
      if (freshInsightsPollSessionRef.current) {
        setInsightsGenerateRecovery(false)
        setMinutesSectionError(null)
        return
      }
      freshInsightsPollSessionRef.current = false
      if (data.durationSeconds > 0) {
        setIsPollingInsights(false)
        setInsightsGenerateRecovery(true)
        setMinutesSectionError(uiStringsRef.current.meetingMinutesUnavailable)
      } else {
        setInsightsGenerateRecovery(false)
      }
      return
    }
    if (data.insightsStatus === 'FAILED') {
      freshInsightsPollSessionRef.current = false
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(!hasNoSpeechFailure(data))
      setMinutesSectionError(minutesFailureMessage(data, uiStringsRef.current))
      return
    }
    if (meetingIsPendingSave || data.insightsStatus === 'PENDING') {
      freshInsightsPollSessionRef.current = true
      setIsPollingInsights(true)
      setInsightsGenerateRecovery(false)
      setMinutesSectionError(null)
      return
    }
    if (freshInsightsPollSessionRef.current) {
      setInsightsGenerateRecovery(false)
      setMinutesSectionError(null)
      return
    }
    setIsPollingInsights(false)
    setInsightsGenerateRecovery(true)
    setMinutesSectionError(uiStringsRef.current.meetingMinutesUnavailable)
  }
  const [minutesSaveError, setMinutesSaveError] = useState<string | null>(null)
  const [titleConflictError, setTitleConflictError] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftProjectKey, setDraftProjectKey] = useState('')
  const projectFieldId = useId()
  const [draftMinutes, setDraftMinutes] = useState<MeetingMinutesData>(EMPTY_MEETING_MINUTES)
  const draftTitleRef = useRef('')
  const draftProjectKeyRef = useRef('')
  const draftMinutesRef = useRef<MeetingMinutesData>(EMPTY_MEETING_MINUTES)
  const [isSavingMinutes, setIsSavingMinutes] = useState(false)
  /**
   * Which parts of the page are open for editing, if any.
   *
   * A single page-wide flag before: pressing `+ Add purpose` turned every
   * field on the screen into an input, which is what BRAI-678 reports. Each
   * `+ Add` now opens its own section and leaves the rest as text, while Edit
   * in the header opens all of them.
   *
   * There is still ONE draft and ONE Save. This decides what is editable, not
   * what is committed — a reader who opens two sections saves both with the
   * same press, and the unsaved-changes count stays a count for the page.
   */
  /**
   * The row a per-section press just created, waiting to be focused.
   *
   * State and a layout effect rather than a frame scheduled from the handler:
   * a timer outlives the page it was scheduled from, and this one would fire
   * into whatever had replaced it. The effect cannot run after unmount, and
   * it runs with the DOM already updated, which is the whole requirement.
   */
  /**
   * The frame on which a per-section press lands the caret in the row it
   * created. Held so it can be cancelled.
   *
   * A frame rather than a layout effect, which would be the obvious choice:
   * the row is not in the DOM in the render that opens the section.
   * `EditableStringList` keeps its own copy of the items and syncs it in a
   * passive effect, so the first render after the press draws the list it had
   * before — a layout effect looks for a field that is one render away.
   *
   * Cancelled on unmount below, because a frame outlives the page that
   * scheduled it and would otherwise put the caret into whatever replaced it.
   */
  const focusFrameRef = useRef<number | null>(null)

  // Up here with the other hooks, not beside `beginEditWith`: that is
  // declared past the early return for a meeting that would not load, and a
  // hook below that line is a hook this component sometimes does not call.
  useEffect(
    () => () => {
      if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
    },
    []
  )

  const [editingSections, setEditingSections] = useState<ReadonlySet<MinutesEditSection>>(
    EMPTY_EDIT_SECTIONS
  )
  /**
   * Whether the editor was opened from the header rather than from one card.
   *
   * The two are different offers, and the Save that fits each differs with
   * them: "edit this page" is answered by one Save in the header, the way it
   * always was, while "add a purpose" is answered by that card's own. Showing
   * both at once would be five Saves on one screen (BRAI-678 review).
   *
   * Set by whichever press opened the editor, and cleared when the last card
   * closes. It cannot change in between: every per-card `+ Add` is drawn only
   * while its card is closed, so with the whole page open there is nothing
   * left to press that would ask the smaller question.
   */
  const [editingWholePage, setEditingWholePage] = useState(false)
  /** True while any section is open: what the header, the layout and the guards read. */
  const isEditMode = editingSections.size > 0
  /** Whether one section is open. Everything a section owns reads this instead. */
  const isEditing = (section: MinutesEditSection) => editingSections.has(section)
  const isEditingActions = editingSections.has('actions')
  const [feedbackEditOpen, setFeedbackEditOpen] = useState(false)
  const [isSavingFeedback, setIsSavingFeedback] = useState(false)
  const [feedbackSaveError, setFeedbackSaveError] = useState<string | null>(null)
  const [isSavingProjectKey, setIsSavingProjectKey] = useState(false)
  const [projectKeySaveError, setProjectKeySaveError] = useState<string | null>(null)
  /** Whether the move-project dialog is open. */
  const [isMoveProjectOpen, setIsMoveProjectOpen] = useState(false)
  const [isMovingProject, setIsMovingProject] = useState(false)
  /**
   * Why the last move came to nothing, if it did.
   *
   * Shown inside the dialog rather than on the page behind it: the reader
   * still has a project picked there and nothing has moved, so the dialog is
   * the only place the message is next to the thing it is about.
   */
  const [moveProjectError, setMoveProjectError] = useState<string | null>(null)
  const [exportingDocx, setExportingDocx] = useState<MeetingMinutesLocale | null>(null)
  const [exportDocxError, setExportDocxError] = useState<string | null>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showShareEmailModal, setShowShareEmailModal] = useState(false)
  const [shareEmailSent, setShareEmailSent] = useState<{
    meetingTitle: string
    emails: string[]
  } | null>(null)
  const exportMenuRef = useRef<HTMLDivElement>(null)
  const uiStringsRef = useRef(t)
  uiStringsRef.current = t
  const meetingRef = useRef(meeting)
  meetingRef.current = meeting
  const pendingSavedMeetings = useStore((s) => s.pendingSavedMeetings)
  const internalMinutesJobId = useStore((s) =>
    id ? s.internalMinutesJobByMeetingId[id] : undefined
  )
  const internalMinutesAudioQueueFailed = useStore((s) =>
    id ? Boolean(s.internalMinutesAudioQueueFailedIds[id]) : false
  )
  const currentUserId = getCurrentUserId()
  const hasPendingSavedMeeting = pendingSavedMeetings.length > 0
  const isMeetingPendingSave = Boolean(
    id && pendingSavedMeetings.some((pending) => pending.clientId === id)
  )

  useEffect(() => {
    if (!id || !internalMinutesJobId) return
    runInternalMinutesJobInBackground(id, internalMinutesJobId)
  }, [id, internalMinutesJobId])

  // Recomputing this by re-filtering/mapping the full transcript on every
  // render gets noticeably slower the longer the meeting is (BRAI-155) — the
  // component re-renders often while editing (draft fields, edit mode), so
  // this must not redo the O(n) transform each time.
  const translationSegments = useMemo(
    () => (meeting ? resolveTranslationSegments(meeting) : []),
    [meeting]
  )

  /**
   * Paired here, above the early returns, because these are hooks — and in
   * the card rather than inside it because the “View full transcript”
   * control sits in the card's *header* and has to know whether anything was
   * actually held back. A link offering to expand a preview that is already
   * complete is a link that does nothing.
   */
  const previewLimit = showFullTranscript ? undefined : 3
  const transcriptTurns = useMemo(
    () => pairStoredTranscriptTurns(meeting?.transcript ?? EMPTY_SEGMENTS, translationSegments),
    [meeting?.transcript, translationSegments]
  )
  const transcriptRows = useMemo(
    () =>
      previewLimit === undefined
        ? transcriptTurns
        : limitToSpeakerRuns(transcriptTurns, previewLimit),
    [transcriptTurns, previewLimit]
  )
  const transcriptIsTruncated = transcriptRows.length < transcriptTurns.length

  // Only while editing: the table is read-only text otherwise, and this is a
  // Graph round trip on the server's cache.
  const { users: directory } = useTenantDirectory(isEditingActions)

  // Above the early returns, as hooks must be. Owner-only because the server
  // refuses a sync from anyone else, so checking as a share recipient would
  // just be a round trip that ends in 403.
  /**
   * Docked inside a Teams call, where leaving this page goes nowhere: the
   * panel is one route deep in a host that owns its own navigation, and the
   * app shell this page's Back button returns to is not rendered there.
   */
  const isTeamsPanel = useStore((s) => s.isTeamsPanel)
  /** Saved rows drive dry-run (server reads the stored meeting). */
  const backlogActionItems = meeting?.insights?.action_items ?? EMPTY_ACTION_ITEMS
  /**
   * The meeting named a Backlog project back in the create form.
   *
   * Next actions is drawn for these meetings only. Trimmed rather than checked
   * for truthiness alone: the field is a free-text key off a form, and a space
   * is not a project.
   */
  const savedProjectKey = meeting?.backlogProjectKey?.trim() ?? ''
  /**
   * What Next actions is drawn against — the unsaved pick while editing.
   *
   * Split from `savedProjectKey` on purpose. The table has to open the moment
   * a project is chosen, before anything is saved; the Backlog calls below
   * must not, because the dry run is answered from the stored meeting and
   * would be checking an unsaved pick against the project it replaced.
   */
  const shownProjectKey = isEditingActions ? draftProjectKey.trim() : savedProjectKey
  const hasBacklogProject = Boolean(shownProjectKey)
  const isBacklogOwner = Boolean(
    id && meeting && currentUserId && meeting.userId === currentUserId,
  )
  /**
   * Connection + roster/meta follow the project Next actions is drawn against
   * (draft while editing). Without this, picking a project for the first time
   * never called GET /members until Save.
   */
  const backlogUiEnabled = Boolean(isBacklogOwner && shownProjectKey)
  /**
   * Dry-run / Push still need a saved project: the server checks the stored
   * meeting, not the unsaved draft pick.
   */
  const backlogSyncEnabled = Boolean(isBacklogOwner && savedProjectKey)
  const backlogConnection = useBacklogConnection({ enabled: backlogUiEnabled })

  /**
   * Persist the values approved in a Backlog overwrite back into Meeting
   * Minutes, so refreshing the page does not restore the pre-confirmation row.
   * Backlog-only fields such as priority and description deliberately stay out
   * of the action-item schema.
   */
  const persistConfirmedBacklogAction = useCallback(
    async (index: number, confirmedItem: MeetingMinutesActionItem) => {
      const current = meetingRef.current
      const currentInsights = current?.insights
      if (!current || !currentInsights?.action_items[index]) {
        throw new Error(uiStringsRef.current.failedSaveMeeting)
      }

      const actionItems = currentInsights.action_items.map((item, itemIndex) =>
        itemIndex === index ? confirmedItem : item,
      )
      const nextInsights = { ...currentInsights, action_items: actionItems }
      const updatedMeeting = await updateMeeting(current.id, { insights: nextInsights })
      const savedInsights = updatedMeeting.insights ?? nextInsights

      setMeeting((existing) =>
        existing?.id === current.id
          ? { ...existing, ...updatedMeeting, insights: savedInsights }
          : existing,
      )

      // Preserve drafts in the other cards (and non-Backlog action fields)
      // while making the just-confirmed values the source for the action row.
      const draft = draftMinutesRef.current
      const draftItem = draft.action_items[index]
      const savedItem = savedInsights.action_items[index] ?? confirmedItem
      if (!draftItem) return
      const nextDraft = {
        ...draft,
        action_items: draft.action_items.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                content: savedItem.content,
                assignee: savedItem.assignee,
                assignee_id: savedItem.assignee_id,
                deadline: savedItem.deadline,
              }
            : item,
        ),
      }
      draftMinutesRef.current = nextDraft
      setDraftMinutes(nextDraft)
    },
    [],
  )

  const backlog = useBacklogSync({
    meetingId: id ?? '',
    actionItems: backlogActionItems,
    enabled: backlogUiEnabled,
    checkEnabled: backlogSyncEnabled,
    connected: backlogConnection.connected,
    // Roster and issue-type names follow what Next actions is drawn against —
    // the unsaved pick while editing — so changing Project refreshes members
    // before Save. Dry-run still reads the stored meeting on the server.
    projectKey: shownProjectKey || null,
    // Push targets the stored project; withhold draft-roster email matches.
    writeProjectKey: savedProjectKey || null,
    onConfirmedActionPersist: persistConfirmedBacklogAction,
  })

  /**
   * Drop assignees that the new project's roster does not contain.
   *
   * Runs after members load rather than on the Project change itself: a person
   * who belongs to both projects should keep their selection, and the roster
   * is the only source of that answer. Display name is cleared with the id so
   * the cell does not keep advertising someone who can no longer be assigned.
   *
   * Gated on `backlogUiEnabled` (owner + shown project) rather than `isOwner`,
   * because this effect sits above the early-return path that declares `isOwner`.
   */
  useEffect(() => {
    if (!isEditingActions || !backlogUiEnabled) return
    if (backlog.membersLoading || backlog.membersError) return
    if (!backlog.config?.assign_enabled || !backlogConnection.connected) return
    if (backlog.members === null) return

    const validIds = new Set(backlog.members.map((member) => member.id))
    const minutes = draftMinutesRef.current
    const { items: nextItems, changed } = clearInvalidBacklogAssignees(
      minutes.action_items,
      validIds,
    )
    if (!changed) return
    const next = { ...minutes, action_items: nextItems }
    draftMinutesRef.current = next
    setDraftMinutes(next)
  }, [
    isEditingActions,
    backlogUiEnabled,
    backlog.members,
    backlog.membersLoading,
    backlog.membersError,
    backlog.config?.assign_enabled,
    backlogConnection.connected,
  ])

  /** Auto-dismisses the duplicate-title toast 5s after it appears. */
  useEffect(() => {
    if (!titleConflictError) return
    const timer = setTimeout(() => setTitleConflictError(null), 5000)
    return () => clearTimeout(timer)
  }, [titleConflictError])

  /** Closes the export-format dropdown when the user clicks anywhere outside it. */
  useEffect(() => {
    if (!showExportMenu) return

    function onDocumentMouseDown(event: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false)
      }
    }

    document.addEventListener('mousedown', onDocumentMouseDown)
    return () => document.removeEventListener('mousedown', onDocumentMouseDown)
  }, [showExportMenu])

  useEffect(() => {
    const meetingId = id
    if (!meetingId) {
      setMeeting(null)
      setLoading(false)
      setError(uiStringsRef.current.meetingNotFound)
      return
    }

    let ignore = false
    const hasCachedMeetingForId = meetingRef.current?.id === meetingId
    if (!hasCachedMeetingForId) {
      setMeeting(null)
      setLoading(true)
      setMinutesSectionError(null)
      setTranscriptSectionError(null)
      setAudioSectionError(null)
      setIsPollingTranscript(false)
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(false)
      setIsGeneratingMinutes(false)
      freshInsightsPollSessionRef.current = false
    }
    setError(null)
    setAccessRemoved(false)

    void fetchMeetingById(meetingId)
      .then((data) => {
        if (ignore) return
        setMeeting(data)
        const meetingIsPendingSave = useStore
          .getState()
          .pendingSavedMeetings.some((pending) => pending.clientId === meetingId)
        markInsightsPollingIfNeeded(data, meetingIsPendingSave)
        setTranscriptSectionError(null)
      })
      .catch((err) => {
        if (ignore) return
        if (err instanceof ApiError && err.status === 403) {
          setAccessRemoved(true)
          setError(uiStringsRef.current.accessRemoved)
          if (!hasCachedMeetingForId) setMeeting(null)
          return
        }
        setError(
          err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
        )
        if (!hasCachedMeetingForId) setMeeting(null)
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })

    return () => {
      ignore = true
    }
  }, [id])

  const meetingId = meeting?.id
  const meetingTranscriptLength = meeting?.transcript.length ?? 0
  const meetingHasInsights = Boolean(meeting?.insights)
  const meetingInsightsStatus = meeting?.insightsStatus ?? 'NONE'
  const meetingMinutesHasNoSpeech = hasNoSpeechFailure(meeting)
  const meetingDurationSeconds = meeting?.durationSeconds ?? 0
  const meetingAudioUrl = meeting?.audioUrl
  const meetingAudioUploadStatus = meeting?.audioUploadStatus

  /**
   * Background poll while a freshly saved meeting still has an empty transcript
   * (native projection delayed). Drives section-level transcript skeleton only.
   */
  useEffect(() => {
    if (!id || !meetingId) return
    const shouldPollTranscript =
      meetingTranscriptLength === 0 &&
      meetingDurationSeconds > 0 &&
      hasPendingSavedMeeting
    if (!shouldPollTranscript) {
      setIsPollingTranscript(false)
      return
    }

    let ignore = false
    const startedAt = Date.now()
    // Fresh session: no previous count to compare against, so the first read can
    // never be mistaken for a settled one.
    lastTranscriptCountRef.current = -1
    setIsPollingTranscript(true)

    const poll = window.setInterval(() => {
      if (Date.now() - startedAt > SUMMARY_POLL_TIMEOUT_MS) {
        window.clearInterval(poll)
        if (!ignore) setIsPollingTranscript(false)
        return
      }

      void fetchMeetingById(id)
        .then((data) => {
          if (ignore) return
          setMeeting(data)
          markInsightsPollingIfNeeded(
            data,
            useStore.getState().pendingSavedMeetings.some((pending) => pending.clientId === id)
          )
          setTranscriptSectionError(null)
          const transcriptReady = isTranscriptPollComplete({
            transcriptLength: data.transcript.length,
            previousTranscriptLength: lastTranscriptCountRef.current,
            durationSeconds: data.durationSeconds,
            hasPendingSave: hasPendingSavedMeeting,
          })
          lastTranscriptCountRef.current = data.transcript.length
          if (transcriptReady) {
            setIsPollingTranscript(false)
            window.clearInterval(poll)
          }
        })
        .catch((err) => {
          if (ignore) return
          setTranscriptSectionError(
            err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
          )
          setIsPollingTranscript(false)
          window.clearInterval(poll)
        })
    }, SUMMARY_POLL_INTERVAL_MS)

    return () => {
      ignore = true
      window.clearInterval(poll)
    }
  }, [
    id,
    meetingId,
    meetingTranscriptLength,
    meetingDurationSeconds,
    hasPendingSavedMeeting,
    transcriptPollToken,
  ])

  /**
   * Background poll when transcript is visible but AI minutes (`insights`) are
   * still null — only while this meeting is a fresh pending save. Stuck meetings
   * without a pending row show generate-recovery Retry instead of waiting 90s.
   */
  useEffect(() => {
    if (!id || !meetingId) return
    if (internalMinutesAudioQueueFailed) {
      freshInsightsPollSessionRef.current = false
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(true)
      setMinutesSectionError((current) => current ?? uiStringsRef.current.meetingMinutesUnavailable)
      return
    }
    const awaitingInsightsPoll =
      isMeetingPendingSave ||
      Boolean(internalMinutesJobId) ||
      meetingInsightsStatus === 'PENDING' ||
      freshInsightsPollSessionRef.current
    if (
      (meetingTranscriptLength === 0 && !awaitingInsightsPoll) ||
      meetingHasInsights
    ) {
      if (meetingHasInsights) {
        freshInsightsPollSessionRef.current = false
      }
      setIsPollingInsights(false)
      if (
        meetingTranscriptLength === 0 &&
        !awaitingInsightsPoll &&
        !meetingHasInsights &&
        meetingDurationSeconds > 0
      ) {
        setInsightsGenerateRecovery(true)
        setMinutesSectionError((current) => current ?? uiStringsRef.current.meetingMinutesUnavailable)
      }
      return
    }
    // A job the server reports as FAILED will never land — stop waiting on it.
    if (meetingInsightsStatus === 'FAILED') {
      freshInsightsPollSessionRef.current = false
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(!meetingMinutesHasNoSpeech)
      setMinutesSectionError(
        (current) =>
          current ??
          (meetingMinutesHasNoSpeech
            ? uiStringsRef.current.meetingMinutesNoSpeech
            : uiStringsRef.current.meetingMinutesUnavailable),
      )
      return
    }
    const insightsJobRunning = meetingInsightsStatus === 'PENDING'
    if (
      !isMeetingPendingSave &&
      !insightsJobRunning &&
      !freshInsightsPollSessionRef.current &&
      !internalMinutesJobId
    ) {
      setIsPollingInsights(false)
      setInsightsGenerateRecovery(true)
      setMinutesSectionError((current) => current ?? uiStringsRef.current.meetingMinutesUnavailable)
      return
    }

    let ignore = false
    const startedAt = Date.now()
    const insightsPollTimeoutMs =
      meetingTranscriptLength === 0
        ? INTERNAL_AUDIO_MINUTES_POLL_TIMEOUT_MS
        : SUMMARY_POLL_TIMEOUT_MS
    if (isMeetingPendingSave || insightsJobRunning || internalMinutesJobId) {
      freshInsightsPollSessionRef.current = true
    }
    setIsPollingInsights(true)
    setInsightsGenerateRecovery(false)

    const poll = window.setInterval(() => {
      if (Date.now() - startedAt > insightsPollTimeoutMs) {
        window.clearInterval(poll)
        if (!ignore) {
          freshInsightsPollSessionRef.current = false
          setIsPollingInsights(false)
          setInsightsGenerateRecovery(true)
          setMinutesSectionError(uiStringsRef.current.meetingMinutesUnavailable)
        }
        return
      }

      void fetchMeetingById(id)
        .then((data) => {
          if (ignore) return
          setMeeting(data)
          if (data.insights) {
            freshInsightsPollSessionRef.current = false
            setInsightsGenerateRecovery(false)
            setMinutesSectionError(null)
            setIsPollingInsights(false)
            window.clearInterval(poll)
            return
          }
          setTranscriptSectionError(null)
        })
        .catch((err) => {
          if (ignore) return
          setMinutesSectionError(
            err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
          )
          setIsPollingInsights(false)
          setInsightsGenerateRecovery(false)
          window.clearInterval(poll)
        })
    }, SUMMARY_POLL_INTERVAL_MS)

    return () => {
      ignore = true
      window.clearInterval(poll)
    }
  }, [
    id,
    meetingId,
    meetingTranscriptLength,
    meetingDurationSeconds,
    meetingHasInsights,
    meetingInsightsStatus,
    meetingMinutesHasNoSpeech,
    isMeetingPendingSave,
    internalMinutesJobId,
    internalMinutesAudioQueueFailed,
    insightsPollToken,
  ])

  /**
   * Keeps refetching audio status so the widget can show the real player or a
   * failure notice instead of a frozen PENDING snapshot.
   */
  useEffect(() => {
    if (!id || !meetingId) return
    const isAudioPending =
      !meetingAudioUrl &&
      (hasPendingSavedMeeting || meetingAudioUploadStatus === 'PENDING')
    if (!isAudioPending) return

    let ignore = false
    const startedAt = Date.now()
    setAudioSectionError(null)

    const poll = window.setInterval(() => {
      if (Date.now() - startedAt > AUDIO_STATUS_POLL_TIMEOUT_MS) {
        window.clearInterval(poll)
        return
      }
      void fetchMeetingAudioStatus(id)
        .then((data) => {
          if (ignore) return
          setAudioSectionError(null)
          setMeeting((current) =>
            current?.id === id
              ? {
                  ...current,
                  audioUrl: data.audioUrl,
                  audioUploadStatus: data.audioUploadStatus,
                }
              : current,
          )
        })
        .catch((err) => {
          if (ignore) return
          setAudioSectionError(
            err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
          )
          window.clearInterval(poll)
        })
    }, AUDIO_STATUS_POLL_INTERVAL_MS)

    return () => {
      ignore = true
      window.clearInterval(poll)
    }
  }, [
    id,
    meetingId,
    meetingAudioUrl,
    meetingAudioUploadStatus,
    hasPendingSavedMeeting,
  ])

  /**
   * Detail poll / soft refetch can land an `audioUrl` or terminal FAILED
   * without going through the audio-status Retry path — drop a stale audio
   * section error so the player / unavailable notice is not hidden behind Retry.
   */
  useEffect(() => {
    if (meetingAudioUrl || meetingAudioUploadStatus === 'FAILED') {
      setAudioSectionError(null)
    }
  }, [meetingAudioUrl, meetingAudioUploadStatus])

  /**
   * Seed the local editable draft (title + minutes) from the freshly loaded
   * meeting. Re-runs when AI-generated insights first arrive (background
   * insights polling keeps `meeting` in sync while this page is open) so a
   * still-pending draft doesn't get stuck on the placeholder
   * `EMPTY_MEETING_MINUTES`. It never re-runs on a later successful save —
   * `handleSaveMinutes` re-syncs the draft itself once the PATCH resolves.
   * `syncedMeetingKeyRef` records which meeting snapshot the draft was last
   * seeded from, so `isMinutesDirty` below can stay `false` for the one
   * render where `meeting` has just changed but this effect hasn't run yet —
   * without it, that render would compare a stale (pre-sync) draft against
   * the new meeting and flash a false "unsaved changes" state.
   */
  const syncedMeetingKeyRef = useRef<string | null>(null)

  /** Soft section refetch shared by Minutes + Transcript Retry (one GET; error scoped to the card). */
  async function refetchMeetingDetail(section: 'minutes' | 'transcript') {
    if (!id) return
    setDetailRefetching(true)
    if (section === 'minutes') setMinutesSectionError(null)
    else setTranscriptSectionError(null)
    try {
      const data = await fetchMeetingById(id)
      setMeeting(data)
      const meetingIsPendingSave = useStore
        .getState()
        .pendingSavedMeetings.some((pending) => pending.clientId === id)
      markInsightsPollingIfNeeded(data, meetingIsPendingSave)
      if (section === 'minutes' && data.insights) setMinutesSectionError(null)
      else if (section === 'transcript') setTranscriptSectionError(null)
      setError(null)
      // Resume pending-section polls after a successful Retry that still lacks
      // data (effect deps would otherwise stay unchanged and never remount).
      // Set polling flags before clearing detailRefetching to avoid a flash of
      // empty editable placeholders between soft-refetch and effect remount.
      if (
        !data.insights &&
        meetingIsPendingSave &&
        (data.transcript.length > 0 || data.durationSeconds > 0)
      ) {
        setIsPollingInsights(true)
        setInsightsGenerateRecovery(false)
        setInsightsPollToken((n) => n + 1)
      }
      if (
        data.transcript.length === 0 &&
        data.durationSeconds > 0 &&
        hasPendingSavedMeeting
      ) {
        setIsPollingTranscript(true)
        setTranscriptPollToken((n) => n + 1)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
      if (section === 'minutes') {
        setMinutesSectionError(message)
        setInsightsGenerateRecovery(false)
      } else setTranscriptSectionError(message)
    } finally {
      setDetailRefetching(false)
    }
  }

  /**
   * Manual recovery when the minutes never landed: hand the job back to the
   * server and let the poll above pick up the result.
   *
   * Only the hand-off is awaited (BRAI-412) — the generation itself outlives
   * this page, so leaving mid-way no longer throws the work away, and the user
   * is not held here watching a spinner for however long the AI takes.
   */
  /**
   * Ask for this meeting's minutes and start polling for them.
   *
   * @param options.regenerate Replace minutes the meeting already has. Without
   *   it the server answers `READY` and does nothing, which is the gate a
   *   meeting that just changed project has to get past.
   */
  async function requestMinutesGeneration(options: { regenerate?: boolean } = {}) {
    if (!id || !meetingRef.current || isGeneratingMinutes) return
    const current = meetingRef.current
    const plainTranscript = segmentsToPlainTranscript(current.transcript)

    setIsGeneratingMinutes(true)
    setMinutesSectionError(null)
    useStore.getState().clearInternalMinutesAudioQueueFailed(id)
    try {
      let insightsStatus: Meeting['insightsStatus']
      if (plainTranscript) {
        ;({ insightsStatus } = await requestMeetingMinutes(id, options))
      } else {
        // No download and no re-upload. Retry used to fetch the whole recording
        // back out of storage only to post the same bytes again; the server
        // reads it from storage itself now, so the recording only has to exist.
        let audioUrl = current.audioUrl
        if (!audioUrl) {
          const audioStatus = await fetchMeetingAudioStatus(id)
          audioUrl = audioStatus.audioUrl
          if (audioUrl) {
            setMeeting((existing) =>
              existing?.id === id
                ? {
                    ...existing,
                    audioUrl,
                    audioUploadStatus: audioStatus.audioUploadStatus,
                  }
                : existing
            )
          }
        }
        if (!audioUrl) {
          setMinutesSectionError(uiStringsRef.current.meetingMinutesUnavailable)
          setInsightsGenerateRecovery(true)
          return
        }
        ;({ insightsStatus } = await requestMeetingMinutesFromAudio(id))
      }
      setMeeting((existing) => (existing ? { ...existing, insightsStatus } : existing))
      setInsightsGenerateRecovery(false)
      setIsPollingInsights(true)
      // Remounts the poll effect even when nothing else it depends on moved —
      // the status it reads was just written by the call above.
      setInsightsPollToken((n) => n + 1)
    } catch (err) {
      setMinutesSectionError(
        err instanceof Error ? err.message : uiStringsRef.current.failedGenerateMeetingMinutes
      )
      setInsightsGenerateRecovery(true)
    } finally {
      setIsGeneratingMinutes(false)
    }
  }

  function handleMinutesRetry() {
    if (insightsGenerateRecovery) {
      void requestMinutesGeneration()
      return
    }
    void refetchMeetingDetail('minutes')
  }

  /** Full-page reload after the initial GET /meetings/:id failed. */
  async function retryInitialMeetingLoad() {
    if (!id) return
    setLoading(true)
    setError(null)
    setMinutesSectionError(null)
    setTranscriptSectionError(null)
    setAudioSectionError(null)
    try {
      const data = await fetchMeetingById(id)
      setMeeting(data)
      markInsightsPollingIfNeeded(
        data,
        useStore.getState().pendingSavedMeetings.some((pending) => pending.clientId === id)
      )
    } catch (err) {
      setMeeting(null)
      setError(
        err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
      )
    } finally {
      setLoading(false)
    }
  }

  async function retryAudioStatus() {
    if (!id) return
    setAudioSectionError(null)
    try {
      const data = await fetchMeetingAudioStatus(id)
      setMeeting((current) =>
        current?.id === id
          ? {
              ...current,
              audioUrl: data.audioUrl,
              audioUploadStatus: data.audioUploadStatus,
            }
          : current,
      )
    } catch (err) {
      setAudioSectionError(
        err instanceof Error ? err.message : uiStringsRef.current.failedLoadMeetingDetails
      )
    }
  }

  function commitDraftTitle(title: string) {
    draftTitleRef.current = title
    setDraftTitle(title)
  }

  function commitDraftMinutes(minutes: MeetingMinutesData) {
    draftMinutesRef.current = minutes
    setDraftMinutes(minutes)
  }

  function commitDraftProjectKey(projectKey: string) {
    draftProjectKeyRef.current = projectKey
    setDraftProjectKey(projectKey)
  }

  /**
   * Move the minutes to another Backlog project, dropping the issue types the
   * rows carry.
   *
   * Issue type is stored as a NAME, and names are declared per project —
   * `POST /tasks` refuses one the project does not define. Carrying them over
   * would leave rows that look settled and fail at Push, so the choice is
   * cleared and made again against the new project's own list.
   *
   * @param projectKey Backlog's key for the project, or `''` for none.
   */
  function changeDraftProjectKey(projectKey: string) {
    if (projectKey === draftProjectKeyRef.current) return
    commitDraftProjectKey(projectKey)
    const minutes = draftMinutesRef.current
    if (!minutes.action_items.some((item) => item.issue_type)) return
    updateDraftMinutes({
      action_items: minutes.action_items.map(({ issue_type: _dropped, ...item }) => item),
    })
  }

  /**
   * Apply `patch` on top of the current unsaved draft (not the last-saved
   * snapshot), so edits from different sections stack instead of clobbering
   * each other before the user clicks Save. The ref is updated synchronously
   * so Save can read committed values even before React re-renders.
   */
  function updateDraftMinutes(patch: Partial<MeetingMinutesData>) {
    const next = { ...draftMinutesRef.current, ...patch }
    draftMinutesRef.current = next
    setDraftMinutes(next)
  }

  useEffect(() => {
    if (!meeting) return
    commitDraftTitle(meeting.title)
    commitDraftProjectKey(meeting.backlogProjectKey ?? '')
    commitDraftMinutes(meeting.insights ?? EMPTY_MEETING_MINUTES)
    syncedMeetingKeyRef.current = `${meeting.id}:${Boolean(meeting.insights)}`
  }, [meeting?.id, Boolean(meeting?.insights)])

  const meetingKey = meeting ? `${meeting.id}:${Boolean(meeting.insights)}` : null
  const isDraftSynced = meetingKey !== null && meetingKey === syncedMeetingKeyRef.current
  /**
   * Whether a draft differs from the meeting as the server last returned it.
   *
   * Takes the three values instead of reading them, because two copies of the
   * draft disagree for exactly one render: the state this component renders
   * from, and the refs `ensureMinutesSaved` reads. A Save click arrives in the
   * same gesture that blurred the field being saved, so when the handler runs
   * only the refs carry that last edit.
   */
  function minutesDifferFromSaved(
    title: string,
    projectKey: string,
    minutes: MeetingMinutesData
  ): boolean {
    return (
      title.trim() !== (meeting?.title ?? '').trim() ||
      projectKey !== (meeting?.backlogProjectKey ?? '') ||
      JSON.stringify(minutes) !== JSON.stringify(meeting?.insights ?? EMPTY_MEETING_MINUTES)
    )
  }

  /**
   * Whether one card's own slice has moved, which is what its Save writes.
   *
   * The page-wide answer above is still the one the nav guard and the
   * “N unsaved changes” line want: a reader walking away from a half-typed
   * topic is leaving work behind whether or not they saved the purpose.
   */
  function sectionDiffersFrom(
    section: MinutesEditSection,
    title: string,
    projectKey: string,
    minutes: MeetingMinutesData
  ): boolean {
    const saved = meeting?.insights ?? EMPTY_MEETING_MINUTES
    if (section === 'info') return title.trim() !== (meeting?.title ?? '').trim()
    if (section === 'purpose')
      return JSON.stringify(minutes.purpose) !== JSON.stringify(saved.purpose)
    if (section === 'topics')
      return JSON.stringify(minutes.topics) !== JSON.stringify(saved.topics)
    // The project picker sits in the Next actions header, so its key is that
    // card's to write.
    return (
      projectKey !== (meeting?.backlogProjectKey ?? '') ||
      JSON.stringify(minutes.action_items) !== JSON.stringify(saved.action_items)
    )
  }

  /** `sectionDiffersFrom` against the draft this render is showing. */
  function sectionDiffers(section: MinutesEditSection): boolean {
    return sectionDiffersFrom(section, draftTitle, draftProjectKey, draftMinutes)
  }

  /**
   * How many parts of the page the draft has moved, for the header's
   * “N unsaved changes” line.
   *
   * Counted per part — the name, the project, the purpose list, each topic,
   * the action items — rather than per keystroke or per field, because that
   * is the granularity a reader can act on: it answers “what have I touched”,
   * not “how much typing have I done”. Deliberately not the gate for Save:
   * `minutesDifferFromSaved` stays the single answer to “is there anything to
   * write”, so a part this misses can never silence the button.
   */
  function countMinutesChanges(
    title: string,
    projectKey: string,
    minutes: MeetingMinutesData
  ): number {
    const saved = meeting?.insights ?? EMPTY_MEETING_MINUTES
    const changed = (left: unknown, right: unknown) =>
      JSON.stringify(left) !== JSON.stringify(right)
    let total = 0
    if (title.trim() !== (meeting?.title ?? '').trim()) total += 1
    if (projectKey !== (meeting?.backlogProjectKey ?? '')) total += 1
    if (changed(minutes.purpose, saved.purpose)) total += 1
    const topicCount = Math.max(minutes.topics.length, saved.topics.length)
    for (let index = 0; index < topicCount; index += 1) {
      if (changed(minutes.topics[index], saved.topics[index])) total += 1
    }
    if (changed(minutes.action_items, saved.action_items)) total += 1
    return total
  }

  const isMinutesDirty =
    isEditMode && isDraftSynced && minutesDifferFromSaved(draftTitle, draftProjectKey, draftMinutes)
  /**
   * Never 0 while `isMinutesDirty` holds: the two are computed from the same
   * draft, so a disagreement would mean the count had missed a part — and
   * “0 unsaved changes” above an enabled Save button is the one thing this
   * line must never say.
   */
  const unsavedChangeCount = isMinutesDirty
    ? Math.max(1, countMinutesChanges(draftTitle, draftProjectKey, draftMinutes))
    : 0
  /**
   * The one thing `isMinutesDirty` cannot account for.
   *
   * The editor's fields buffer their keystrokes and only hand the value up on
   * blur, so while one has focus the flag above is a render behind what the
   * reader can see. See {@link isMinutesSaveDisabled}.
   */
  const editableFieldFocused = useEditableFieldFocused()

  /**
   * While the project roster is in flight, Save must not write `assignee_id`
   * values that still belong to the previous project under the new
   * `backlogProjectKey`. A roster *error* does not lock Save — Retry on the
   * picker recovers members; title/minutes edits must still persist.
   */
  const backlogAssigneeRosterBlockingSave = Boolean(
    backlogUiEnabled &&
      backlog.config?.assign_enabled &&
      backlogConnection.connected &&
      shownProjectKey &&
      (backlog.membersLoading || (backlog.members === null && !backlog.membersError)),
  )

  /** Warns before a full reload/tab close while minutes edits are unsaved. */
  useEffect(() => {
    if (!isMinutesDirty) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isMinutesDirty])

  /**
   * Intercept the browser Back button (and any other history pop) while
   * minutes edits are unsaved. `<BrowserRouter>` doesn't support `useBlocker`,
   * so we push a sentinel history entry and re-push it on cancel — same
   * technique as the recording-in-progress guard in `MeetingChat`.
   */
  useEffect(() => {
    if (!isMinutesDirty) return

    window.history.pushState(null, '', window.location.href)

    const handlePopState = () => {
      const confirmed = window.confirm(t.unsavedMinutesChangesPrompt)
      if (confirmed) {
        window.removeEventListener('popstate', handlePopState)
        window.history.back()
        return
      }
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [isMinutesDirty, t.unsavedMinutesChangesPrompt])

  if (!loading && (error || !meeting)) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
        {accessRemoved ? (
          <>
            <p className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)]">
              <span aria-hidden>🔒</span>
              {t.accessRemoved}
            </p>
            {!isTeamsPanel && (
              <Link
                to="/history"
                className="text-sm font-semibold text-[var(--accent)] hover:underline"
              >
                {t.backToMeetings}
              </Link>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-red-500">{error ?? t.meetingNotFound}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {id ? (
                <button
                  type="button"
                  onClick={() => void retryInitialMeetingLoad()}
                  className="btn-primary rounded-md px-4 py-2 text-sm"
                >
                  {t.feedbackRetry}
                </button>
              ) : null}
              {!isTeamsPanel && (
                <Link to={backPath} className="text-sm font-semibold text-[var(--accent)] hover:underline">
                  {t.backToMeetings}
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  // One page shell only. Cold start used to return a second Minutes/Transcript
  // tree; switching trees under AppLayout `<main>` could leave both visible.
  // Skeleton is gated strictly on missing section data.
  const currentMeeting = meeting
  const meetingArtifactsAvailable = currentMeeting?.completed !== false
  /** Share recipients are view-only; mutations require meeting ownership. */
  const isOwner = Boolean(
    currentMeeting && currentUserId && currentMeeting.userId === currentUserId
  )
  /**
   * Whether Next actions draws the project picker itself.
   *
   * Read in three places — the picker, the `Project: X` label it would
   * duplicate, and the sentence shown to whoever gets neither — so it is one
   * flag rather than the same condition spelt three ways.
   */
  const showsProjectPicker = isOwner && (isEditingActions || !savedProjectKey)
  /**
   * Whether the owner is offered the move at all.
   *
   * A meeting with no project has nothing to move — that is a first pick, and
   * the picker above is the control for it. Editing swaps this whole cluster
   * for the editor's own controls, so there is nothing to show then either.
   * Being busy is not one of these reasons; it is `moveProjectBusy` below.
   */
  const canMoveProject = isOwner && Boolean(savedProjectKey) && !isEditMode
  /**
   * Whether the move is unavailable *right now*, as opposed to not offered.
   *
   * A move can ask for the minutes to be written again, so it must not start
   * while they are already being written. `isGeneratingMinutes` alone does not
   * cover that: it is true only for the request that *starts* the job, which
   * returns in a moment, and the writing then runs on the server for as long as
   * the poll lasts. Gating on the poll too is what keeps the button shut for
   * the whole wait instead of re-opening a blink after the click.
   *
   * Deliberately not `insightsStatus === 'PENDING'`: a stuck meeting keeps that
   * status for good and the button would never come back. The poll always ends,
   * so when it gives up the owner has the control again — which is right, since
   * moving and regenerating is one of the ways out of being stuck.
   */
  const moveProjectBusy = isGeneratingMinutes || isPollingInsights
  const hasFeedback =
    Boolean(currentMeeting?.feedbackRating) || Boolean(currentMeeting?.feedbackComment)
  const displayTitle = currentMeeting
    ? isDraftSynced
      ? draftTitle
      : currentMeeting.title
    : ''
  const minutes = currentMeeting
    ? isDraftSynced
      ? draftMinutes
      : (currentMeeting.insights ?? EMPTY_MEETING_MINUTES)
    : EMPTY_MEETING_MINUTES
  // Insights arrive as one object — pending only gates purpose/topics/action_items,
  // not Meeting Info. Include transcript poll so empty-transcript fresh saves keep
  // insights skeleton until native transcript lands and insights poll can start.
  // After poll timeout / stuck meeting (flags clear, insights still null) → Retry generate.
  const insightsPending =
    !currentMeeting?.insights &&
    (!currentMeeting ||
      loading ||
      isPollingInsights ||
      isPollingTranscript ||
      detailRefetching ||
      isGeneratingMinutes)
  const showTranscriptSkeleton =
    !(currentMeeting && currentMeeting.transcript.length > 0) &&
    (!currentMeeting || loading || isPollingTranscript || detailRefetching)
  const showMinutesError =
    Boolean(currentMeeting) && Boolean(minutesSectionError) && !insightsPending
  const showTranscriptError =
    Boolean(currentMeeting) && Boolean(transcriptSectionError) && !showTranscriptSkeleton
  const showAudioSkeleton =
    Boolean(currentMeeting) &&
    !currentMeeting?.audioUrl &&
    !audioSectionError &&
    (hasPendingSavedMeeting || currentMeeting?.audioUploadStatus === 'PENDING')
  const minutesActionsDisabled =
    !currentMeeting || insightsPending || showMinutesError
  const canEditMinutes = isOwner && meetingArtifactsAvailable && !minutesActionsDisabled

  /**
   * Read-only done toggle: optimistic PATCH of insights only. Edit mode flips
   * `done` via the draft `onChange` path and waits for Save.
   */
  /**
   * Persist dirty title/minutes when needed before Save / Share / Export.
   * Returns true when it is safe to continue (nothing to save, or save succeeded).
   *
   * Editable fields only commit on blur, so we blur the focused field first
   * and then read the refs (updated synchronously in onCommit) rather than
   * the possibly-stale render closure. A failed save leaves in-progress edits
   * intact instead of rolling them back.
   *
   * The blur comes before the question "is anything dirty", not after. This is
   * reached from a click on Save, and that click is the same gesture that
   * blurs the field it is saving: at this point `isMinutesDirty` still
   * describes the draft as it stood before the user's last keystrokes, and
   * answering from it would drop them (BRAI-691).
   */
  /**
   * Write one card's edits, and only that card's.
   *
   * The payload is the meeting as the server last returned it with this
   * card's slice of the draft laid over it: a purpose saved while a topic is
   * half-typed writes the purpose and leaves the topic where it is, on screen
   * and unsaved (BRAI-678 review).
   *
   * @returns Whether the card is now saved. `false` is a refusal the reader
   *   can see — a failed write, or a gate that is not open yet — and leaves
   *   the card open with the draft intact to retry from.
   */
  async function saveSection(section: MinutesEditSection): Promise<boolean> {
    if (!currentMeeting) return false

    const active = document.activeElement
    if (active instanceof HTMLElement) active.blur()

    if (!isDraftSynced) return true

    const savedMinutes = currentMeeting.insights ?? EMPTY_MEETING_MINUTES
    const draft = draftMinutesRef.current
    const title = section === 'info' ? draftTitleRef.current : currentMeeting.title
    const projectKey =
      section === 'actions'
        ? draftProjectKeyRef.current
        : (currentMeeting.backlogProjectKey ?? '')

    if (!sectionDiffersFrom(section, draftTitleRef.current, draftProjectKeyRef.current, draft)) {
      closeSection(section);
      return true
    }
    if (isSavingMinutes) return false

    let sectionSlice: Partial<MeetingMinutesData> = {}
    let minutesToSave = savedMinutes
    if (section === 'purpose') {
      sectionSlice = { purpose: draft.purpose }
      minutesToSave = { ...savedMinutes, purpose: draft.purpose }
    } else if (section === 'topics') {
      sectionSlice = { topics: draft.topics }
      minutesToSave = { ...savedMinutes, topics: draft.topics }
    } else if (section === 'actions') {
      // Same gate as this card's Save: do not persist until the roster can
      // validate assignees for the project about to be written.
      if (backlogAssigneeRosterBlockingSave) return false
      let items = draft.action_items
      // Strip stale ids here (not only in the roster-load effect) so a save
      // that races the effect cannot write the previous project's
      // assignee_id under the new backlogProjectKey.
      if (
        backlog.config?.assign_enabled &&
        backlogConnection.connected &&
        backlog.members &&
        !backlog.membersLoading &&
        !backlog.membersError
      ) {
        const validIds = new Set(backlog.members.map((member) => member.id))
        const cleaned = clearInvalidBacklogAssignees(items, validIds)
        if (cleaned.changed) {
          items = cleaned.items
          commitDraftMinutes({ ...draftMinutesRef.current, action_items: items })
        }
      }
      sectionSlice = { action_items: items }
      minutesToSave = { ...savedMinutes, action_items: items }
    }

    // Only this card's own fields. Checking the whole of the minutes would
    // let a purpose stored over-length last week stand between the reader and
    // renaming the meeting — a card can only answer for what it is saving.
    const violation = findMinutesLengthViolation(
      section === 'info' ? title : '',
      section === 'info' ? EMPTY_MEETING_MINUTES : { ...EMPTY_MEETING_MINUTES, ...sectionSlice },
      {
        meetingNameTooLong: t.meetingNameTooLong,
        fieldTooLong: (max) => fmt(t.fieldTooLong, { max }),
      }
    )
    if (violation) {
      setMinutesSaveError(violation.message)
      focusMinutesField(violation.field)
      return false
    }
    setMinutesSaveError(null)
    setTitleConflictError(null)
    setIsSavingMinutes(true)

    try {
      const updatedMeeting = await updateMeeting(currentMeeting.id, {
        ...(section === 'info' ? { title } : {}),
        ...(section === 'actions' ? { backlogProjectKey: projectKey } : {}),
        ...(section === 'info' ? {} : { insights: minutesToSave }),
      })
      // `updatedMeeting` omits transcript/translationSegments (can reach
      // several MB for long meetings) — merge onto the existing meeting
      // instead of replacing it, so those fields are kept intact.
      setMeeting({ ...currentMeeting, ...updatedMeeting })
      // Only this card's slice is re-seeded from the answer. Committing the
      // whole of it would hand the other cards the server's copy and throw
      // away whatever the reader has typed into them.
      const savedBack = updatedMeeting.insights ?? EMPTY_MEETING_MINUTES
      if (section === 'info') {
        commitDraftTitle(updatedMeeting.title)
      } else if (section === 'purpose') {
        commitDraftMinutes({ ...draftMinutesRef.current, purpose: savedBack.purpose })
      } else if (section === 'topics') {
        commitDraftMinutes({ ...draftMinutesRef.current, topics: savedBack.topics })
      } else {
        commitDraftProjectKey(updatedMeeting.backlogProjectKey ?? '')
        commitDraftMinutes({ ...draftMinutesRef.current, action_items: savedBack.action_items })
      }
      closeSection(section)
      return true
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MEETING_TITLE_TAKEN_IN_TRASH') {
        setTitleConflictError(t.meetingNameTakenInTrash)
        return false
      }
      if (err instanceof ApiError && err.code === 'MEETING_TITLE_TAKEN') {
        setTitleConflictError(t.meetingTitleTakenError)
        return false
      }
      setMinutesSaveError(err instanceof Error ? err.message : t.failedSaveMeeting)
      return false
    } finally {
      setIsSavingMinutes(false)
    }
  }

  /** Save one card and say so in the activity log, which the header's Save used to do. */
  async function handleSaveSection(section: MinutesEditSection) {
    const saved = await saveSection(section)
    logActivity('minutes.saved', {
      meetingId: currentMeeting?.id ?? null,
      projectKey: draftProjectKeyRef.current || null,
      status: saved ? 'success' : 'failure',
    })
  }

  /**
   * Write the title, the project and all the minutes at once, and close the
   * editor — what the header's Save has always done.
   *
   * Still here beside `saveSection` because the header still offers to edit
   * the page as one thing. A reader who took that offer gets the Save that
   * matches it.
   */
  async function saveWholePage(): Promise<boolean> {
    if (!currentMeeting) return false

    const active = document.activeElement
    if (active instanceof HTMLElement) active.blur()

    const title = draftTitleRef.current
    let minutesToSave = draftMinutesRef.current
    if (!isDraftSynced) return true
    if (!minutesDifferFromSaved(title, draftProjectKeyRef.current, minutesToSave)) return true
    if (isSavingMinutes) return false
    // Same gate as the Save button: do not persist until the roster can
    // validate assignees for the project about to be written.
    if (backlogAssigneeRosterBlockingSave) return false
    // Strip stale ids here (not only in the roster-load effect) so a Save that
    // races the effect cannot write the previous project's assignee_id under
    // the new backlogProjectKey.
    if (
      backlog.config?.assign_enabled &&
      backlogConnection.connected &&
      backlog.members &&
      !backlog.membersLoading &&
      !backlog.membersError
    ) {
      const validIds = new Set(backlog.members.map((member) => member.id))
      const { items, changed } = clearInvalidBacklogAssignees(
        minutesToSave.action_items,
        validIds,
      )
      if (changed) {
        minutesToSave = { ...minutesToSave, action_items: items }
        commitDraftMinutes(minutesToSave)
      }
    }
    const violation = findMinutesLengthViolation(title, minutesToSave, {
      meetingNameTooLong: t.meetingNameTooLong,
      fieldTooLong: (max) => fmt(t.fieldTooLong, { max }),
    })
    if (violation) {
      setMinutesSaveError(violation.message)
      focusMinutesField(violation.field)
      return false
    }
    setMinutesSaveError(null)
    setTitleConflictError(null)
    setIsSavingMinutes(true)

    try {
      const updatedMeeting = await updateMeeting(currentMeeting.id, {
        title,
        backlogProjectKey: draftProjectKeyRef.current,
        insights: minutesToSave,
      })
      setMeeting({ ...currentMeeting, ...updatedMeeting })
      commitDraftTitle(updatedMeeting.title)
      commitDraftProjectKey(updatedMeeting.backlogProjectKey ?? '')
      commitDraftMinutes(updatedMeeting.insights ?? EMPTY_MEETING_MINUTES)
      setEditingSections(EMPTY_EDIT_SECTIONS)
      setEditingWholePage(false)
      return true
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MEETING_TITLE_TAKEN_IN_TRASH') {
        setTitleConflictError(t.meetingNameTakenInTrash)
        return false
      }
      if (err instanceof ApiError && err.code === 'MEETING_TITLE_TAKEN') {
        setTitleConflictError(t.meetingTitleTakenError)
        return false
      }
      setMinutesSaveError(err instanceof Error ? err.message : t.failedSaveMeeting)
      return false
    } finally {
      setIsSavingMinutes(false)
    }
  }

  async function handleSaveMinutes() {
    const saved = await saveWholePage()
    logActivity('minutes.saved', {
      meetingId: currentMeeting?.id ?? null,
      projectKey: draftProjectKeyRef.current || null,
      status: saved ? 'success' : 'failure',
    })
  }

  /**
   * Throws the whole draft away and closes the editor — the header's Cancel,
   * for the edit the header started.
   */
  function handleCancelEdit() {
    if (!currentMeeting) return
    commitDraftTitle(currentMeeting.title)
    commitDraftProjectKey(currentMeeting.backlogProjectKey ?? '')
    commitDraftMinutes(currentMeeting.insights ?? EMPTY_MEETING_MINUTES)
    setMinutesSaveError(null)
    setTitleConflictError(null)
    setEditingSections(EMPTY_EDIT_SECTIONS)
    setEditingWholePage(false)
  }

  /**
   * Flush whatever is open, for the flows that must not run over unsaved
   * work: Export, Share and Push.
   *
   * Stops at the first refusal rather than writing what it can — a reader
   * whose topic was rejected for length should not find their purpose
   * exported without it.
   */
  async function ensureMinutesSaved(): Promise<boolean> {
    if (editingWholePage) return saveWholePage()
    for (const section of ALL_EDIT_SECTIONS) {
      if (!editingSections.has(section)) continue
      if (!(await saveSection(section))) return false
    }
    return true
  }

  async function openShareMinutesEmail() {
    if (!(await ensureMinutesSaved())) return
    setShowShareEmailModal(true)
  }

  /** Takes one card out of edit mode, leaving every other card as it was. */
  function closeSection(section: MinutesEditSection) {
    setEditingSections((current) => {
      const next = new Set(current)
      next.delete(section)
      if (next.size === 0) setEditingWholePage(false)
      return next
    })
  }

  /**
   * Throws away one card's edits and closes it.
   *
   * Only that card's: Cancel on the purpose has nothing to say about a topic
   * the reader is still writing.
   */
  function cancelSection(section: MinutesEditSection) {
    if (!currentMeeting) return
    const saved = currentMeeting.insights ?? EMPTY_MEETING_MINUTES
    const draft = draftMinutesRef.current
    if (section === 'info') {
      commitDraftTitle(currentMeeting.title)
    } else if (section === 'purpose') {
      commitDraftMinutes({ ...draft, purpose: saved.purpose })
    } else if (section === 'topics') {
      commitDraftMinutes({ ...draft, topics: saved.topics })
    } else {
      commitDraftProjectKey(currentMeeting.backlogProjectKey ?? '')
      commitDraftMinutes({ ...draft, action_items: saved.action_items })
    }
    setMinutesSaveError(null)
    setTitleConflictError(null)
    closeSection(section)
  }

  /**
   * Opens the editor, optionally with `prepare` run against the draft first.
   *
   * Every `+ Add` control on the read-only page comes through here: pressing
   * one has to both open the editor and create the row it promised, and doing
   * only the first leaves the reader looking for a field that is not there.
   *
   * `sections` is what the press opens. Added to whatever is already open
   * rather than replacing it, so opening a second one does not close the
   * first and lose nothing the reader has typed into it.
   * `updateDraftMinutes` writes its ref synchronously, so the row is in the
   * draft before the re-render that shows the fields.
   *
   * `focusField` is the `data-minutes-field` of the row the press creates,
   * landed on a frame later for the reason {@link focusFrameRef} gives.
   * Focusing scrolls it into view as well, which is the point — opening the
   * editor takes the right rail away and widens the column, so the card the
   * reader pressed in has moved by the time they look back at it.
   */
  function beginEditWith(
    sections: readonly MinutesEditSection[],
    prepare?: () => void,
    focusField?: string,
    focusBlock: ScrollLogicalPosition = 'nearest'
  ) {
    commitDraftTitle(clampToMaxLength(draftTitleRef.current, MEETING_TITLE_MAX_LENGTH))
    setEditingWholePage(sections.length === ALL_EDIT_SECTIONS.length)
    setEditingSections((current) => new Set([...current, ...sections]))
    prepare?.()
    if (focusField) {
      if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current)
      focusFrameRef.current = requestAnimationFrame(() => {
        focusFrameRef.current = null
        focusMinutesField(focusField, focusBlock)
      })
    }
  }

  /** Confirms before leaving the page in-app while minutes edits are unsaved. */
  function handleBack() {
    if (isMinutesDirty && !window.confirm(t.unsavedMinutesChangesPrompt)) return
    navigate(backPath)
  }

  /**
   * Persist an edited feedback rating/comment for a meeting that already has
   * feedback. Submitted explicitly via `MeetingFeedbackModal`'s form (already
   * click-to-save, unlike the old minutes auto-save), so it keeps its own
   * optimistic-update-with-rollback + error/loading state, independent of
   * `ensureMinutesSaved`.
   */
  async function saveFeedback(payload: { rating: number; comment: string }) {
    if (!currentMeeting) return
    const previousMeeting = currentMeeting
    // Feedback is optional now (skippable) — the backend rejects
    // `feedbackRating: 0` (@Min(1)), so store/send `null`/omit instead of an
    // empty rating/comment.
    const feedbackRating = payload.rating > 0 ? payload.rating : null
    const feedbackComment = payload.comment || null
    setFeedbackSaveError(null)
    setIsSavingFeedback(true)
    setMeeting({
      ...currentMeeting,
      feedbackRating,
      feedbackComment,
    })

    try {
      const updatedMeeting = await updateMeeting(currentMeeting.id, {
        feedbackRating: feedbackRating ?? undefined,
        feedbackComment: feedbackComment ?? undefined,
      })
      // `updatedMeeting` omits transcript/translationSegments — merge onto
      // the existing meeting instead of replacing it outright.
      setMeeting({ ...currentMeeting, ...updatedMeeting })
      setFeedbackEditOpen(false)
    } catch (err) {
      setMeeting(previousMeeting)
      setFeedbackSaveError(err instanceof Error ? err.message : t.failedSaveMeeting)
    } finally {
      setIsSavingFeedback(false)
    }
  }

  /**
   * Attach a Backlog project to this meeting straight away, from the picker
   * the Next actions card shows while it has none.
   *
   * Saved on the spot rather than into the draft. The draft is what the
   * page-wide editor commits on Save, and routing a first project through it
   * meant the only way to choose one was to press Edit first — which is what
   * BRAI-678 asks to be rid of. Everything else about the project still goes
   * through the draft; this is the empty case alone.
   *
   * Optimistic, and rolled back on failure, like the feedback save above: the
   * action table opens on the project, so waiting for the round trip would
   * leave the card looking inert after a deliberate choice.
   *
   * @param projectKey Backlog's key for the project; `''` is ignored, since
   *   clearing is an edit and belongs in the editor with the rest.
   */
  async function saveProjectKeyNow(projectKey: string) {
    const trimmed = projectKey.trim()
    if (!currentMeeting || !trimmed || isSavingProjectKey) return

    const previousMeeting = currentMeeting
    setProjectKeySaveError(null)
    setIsSavingProjectKey(true)
    setMeeting({ ...currentMeeting, backlogProjectKey: trimmed })

    try {
      const updatedMeeting = await updateMeeting(currentMeeting.id, {
        backlogProjectKey: trimmed,
      })
      // `updatedMeeting` omits transcript/translationSegments — merge rather
      // than replace, the same way the feedback save does.
      setMeeting({ ...currentMeeting, ...updatedMeeting })
    } catch (err) {
      setMeeting(previousMeeting)
      setProjectKeySaveError(err instanceof Error ? err.message : t.failedSaveMeeting)
    } finally {
      setIsSavingProjectKey(false)
    }
  }

  /**
   * Move the meeting onto another project, and write the minutes again when
   * that was asked for.
   *
   * One errand for the reader, two calls here, and the order is not a choice:
   * the minutes job reads the project off the row, so the row has to name the
   * new project before the regenerate is asked for. The same ordering is what
   * lets the server tell the old project to forget the meeting — it refuses
   * while the row still points at it.
   *
   * @param projectKey Project picked in the dialog.
   * @param regenerate Whether to write the minutes again under it.
   */
  async function moveMeetingProject(projectKey: string, regenerate: boolean) {
    const current = meetingRef.current
    const trimmed = projectKey.trim()
    if (!current || !trimmed || isMovingProject) return

    setIsMovingProject(true)
    setMoveProjectError(null)
    try {
      // Cleared here rather than when the server says so. Two round trips sit
      // between the press and the answer — the project write, then the minutes
      // request — and the first one's response still carries the old
      // `insights`, because the server drops them when the job starts. Left to
      // settle on its own, the minutes the reader asked to replace stay on
      // screen for both of them, and the page only admits to working once it
      // is nearly done.
      if (regenerate) {
        setMeeting({ ...current, insights: null, insightsStatus: 'PENDING' })
      }
      const updatedMeeting = await updateMeeting(current.id, {
        backlogProjectKey: trimmed,
      })
      // `updatedMeeting` carries the minutes the server still holds, so the
      // merge order matters: the optimistic clear goes back on top of it.
      setMeeting({
        ...current,
        ...updatedMeeting,
        ...(regenerate ? { insights: null, insightsStatus: 'PENDING' as const } : {}),
      })
      commitDraftProjectKey(updatedMeeting.backlogProjectKey ?? '')
      setIsMoveProjectOpen(false)
      if (regenerate) await requestMinutesGeneration({ regenerate: true })
    } catch (err) {
      // The server still has them: nothing was asked to clear them, or the
      // asking failed. Putting them back is the difference between a failed
      // move and a reader who thinks their minutes are gone.
      setMeeting(current)
      setMoveProjectError(
        err instanceof Error
          ? err.message
          : uiStringsRef.current.meetingProjectMoveFailed
      )
    } finally {
      setIsMovingProject(false)
    }
  }

  async function handleExportDocx(locale: MeetingMinutesLocale) {
    if (!currentMeeting || exportingDocx) return
    if (!(await ensureMinutesSaved())) return
    setShowExportMenu(false)
    setExportDocxError(null)
    setExportingDocx(locale)
    try {
      const { blob, filename } = await buildMinutesDocxBlob(locale)
      downloadBlob(blob, filename)
      logActivity('minutes.exported_docx', { meetingId: currentMeeting.id })
    } catch (err) {
      logActivity('minutes.exported_docx', {
        meetingId: currentMeeting.id,
        status: 'failure',
      })
      setExportDocxError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExportingDocx(null)
    }
  }

  /**
   * Builds the same DOCX as Export for the given locale (translate when needed).
   * Uses draft refs so content matches the last save / on-screen minutes.
   */
  async function buildMinutesDocxBlob(locale: MeetingMinutesLocale): Promise<{
    blob: Blob
    filename: string
  }> {
    if (!currentMeeting) {
      throw new Error(t.failedLoadMeetingDetails)
    }
    const exportTitle = draftTitleRef.current.trim() || currentMeeting.title
    const exportSourceMinutes = draftMinutesRef.current
    const exportMinutes =
      locale === currentMeeting.targetLang
        ? exportSourceMinutes
        : preserveActionItemDone(
            exportSourceMinutes,
            await translateMeetingMinutes({
              language: locale,
              meeting_minutes: exportSourceMinutes,
            })
          )
    const blob = await meetingMinutesToDocxBlob(exportMinutes, {
      title: exportTitle,
      createdAt: currentMeeting.createdAt,
      durationSeconds: currentMeeting.durationSeconds,
      locale,
    })
    const safeName = sanitizeFilename(exportTitle)
    return { blob, filename: `${safeName}-minutes-${locale}.docx` }
  }

  async function handleShareMinutesByEmail(payload: ShareMinutesEmailPayload) {
    if (!currentMeeting) {
      throw new Error(t.failedLoadMeetingDetails)
    }
    // Counted on the send rather than on the button that opens the dialog: a
    // dialog opened and abandoned is not a minute shared (BRAI-677). It
    // rethrows, which is what the dialog shows the reader, so the failure is
    // recorded on the way past.
    try {
      const { blob, filename } = await buildMinutesDocxBlob(payload.locale)
      await shareMeetingMinutesByEmail(currentMeeting.id, {
        locale: payload.locale,
        emails: payload.emails,
        attachment: blob,
        filename,
      })
    } catch (error) {
      logActivity('minutes.shared_by_email', {
        meetingId: currentMeeting.id,
        projectKey: currentMeeting.backlogProjectKey ?? null,
        status: 'failure',
      })
      throw error
    }
    logActivity('minutes.shared_by_email', {
      meetingId: currentMeeting.id,
      projectKey: currentMeeting.backlogProjectKey ?? null,
    })
    setShowShareEmailModal(false)
    setShareEmailSent({
      meetingTitle: displayTitle || currentMeeting.title,
      emails: payload.emails,
    })
  }

  return (
    <>
      {titleConflictError && (
        <div
          role="status"
          className="fixed left-4 right-4 top-4 z-[60] rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-lg sm:left-auto sm:w-96"
        >
          <div className="flex items-start justify-between gap-3">
            <p>{titleConflictError}</p>
            <button
              type="button"
              onClick={() => setTitleConflictError(null)}
              className="shrink-0 text-red-500 hover:text-red-700"
              aria-label={t.close}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <header
        className="flex shrink-0 flex-wrap items-center gap-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 sm:px-6"
        aria-busy={!currentMeeting || undefined}
        aria-label={!currentMeeting ? t.loadingMeeting : undefined}
      >
        <SidebarToggle />
        {!isTeamsPanel && (
          <button
            type="button"
            onClick={handleBack}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
            aria-label={t.backToMeetings}
          >
            <BackIcon className="h-4 w-4" />
          </button>
        )}

        {currentMeeting ? (
          <>
            {isEditMode && (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--accent)]">
                <EditIcon className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-lg font-bold text-[var(--text-primary)]">
                {isEditMode ? fmt(t.editingMeetingTitle, { title: displayTitle }) : displayTitle}
              </h1>
              {isEditMode ? (
                unsavedChangeCount > 0 && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-amber-600">
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      aria-hidden="true"
                    />
                    {unsavedChangeCount === 1
                      ? t.unsavedChangeOne
                      : fmt(t.unsavedChangesMany, { count: unsavedChangeCount })}
                  </p>
                )
              ) : (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
                  <span>{formatDate(currentMeeting.createdAt, locale)}</span>
                  <span aria-hidden="true">•</span>
                  <span>{formatDurationLabel(currentMeeting.durationSeconds, t)}</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                      AUTO
                    </span>
                    <span aria-hidden="true">→</span>
                    <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {getLanguageLabel(currentMeeting.targetLang)}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {/* The project the meeting is filed under, moved up here from the
                  Next actions card (BRAI-678 put it there). It describes the
                  whole meeting — the minutes are written under it and the
                  tickets are pushed to it — so burying it in one card read as
                  a setting that belonged to that card alone. */}
              {showsProjectPicker ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {isEditingActions ? (
                    <ProjectSelect
                      compact
                      id={projectFieldId}
                      value={draftProjectKey}
                      onChange={changeDraftProjectKey}
                      disabled={isSavingMinutes}
                    />
                  ) : (
                    /* Saves on pick, with no Save to press: choosing a project
                       was only reachable through the page-wide editor, so the
                       card asked for something it would not let you do
                       (BRAI-678). */
                    <ProjectSelect
                      compact
                      id={projectFieldId}
                      value=""
                      onChange={(projectKey) => void saveProjectKeyNow(projectKey)}
                      disabled={isSavingProjectKey}
                    />
                  )}
                  {projectKeySaveError && (
                    <p role="alert" className="text-xs text-red-600">
                      {projectKeySaveError}
                    </p>
                  )}
                </div>
              ) : hasBacklogProject ? (
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="text-xs text-[var(--text-secondary)]">
                    {`${t.projectLabel}: ${shownProjectKey}`}
                  </span>
                  {canMoveProject && (
                    <button
                      type="button"
                      onClick={() => {
                        setMoveProjectError(null)
                        setIsMoveProjectOpen(true)
                      }}
                      disabled={moveProjectBusy}
                      title={moveProjectBusy ? t.meetingProjectMoveBusyTooltip : undefined}
                      className="rounded-md border border-[var(--border-color)] px-2 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t.meetingProjectMoveOpen}
                    </button>
                  )}
                </div>
              ) : null}
              {isEditMode ? (
                <>
                  {/* Only for the edit this header started. A card opened on
                      its own carries its own pair, and Export and Share stay
                      out of reach either way (BRAI-678 review). */}
                  {editingWholePage && (
                    <>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={isSavingMinutes}
                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {t.cancel}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleSaveMinutes()}
                        // Believed only when no field is mid-edit: the whole
                        // of why is in `isMinutesSaveDisabled`, between
                        // BRAI-691 and BRAI-704.
                        disabled={isMinutesSaveDisabled({
                          saving: isSavingMinutes,
                          rosterBlocking: backlogAssigneeRosterBlockingSave,
                          dirty: isMinutesDirty,
                          mayHoldUncommittedEdit: editableFieldFocused,
                        })}
                        className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <CheckIcon className="h-3.5 w-3.5" />
                        {isSavingMinutes ? t.saving : t.save}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  {canEditMinutes && (
                    <button
                      type="button"
                      onClick={() => beginEditWith(ALL_EDIT_SECTIONS)}
                      className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"
                    >
                      <EditIcon className="h-3.5 w-3.5" />
                      {t.edit}
                    </button>
                  )}
                  {meetingArtifactsAvailable && (
                    <div className="relative" ref={exportMenuRef}>
                      <button
                        type="button"
                        onClick={() => setShowExportMenu((open) => !open)}
                        disabled={Boolean(exportingDocx) || insightsPending}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <DownloadIcon className="h-3.5 w-3.5" />
                        {exportingDocx
                          ? `${t.exportDocx} (${EXPORT_LOCALE_LABELS[exportingDocx]})...`
                          : t.exportDocx}
                      </button>
                      {showExportMenu && !exportingDocx && (
                        <div className="absolute right-0 z-10 mt-1 min-w-[12rem] overflow-hidden rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-lg">
                          <button
                            type="button"
                            onClick={() => void handleExportDocx('ja')}
                            className="block w-full px-3 py-2 text-left text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
                          >
                            日本語 (Japanese)
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleExportDocx('vi')}
                            className="block w-full border-t border-[var(--border-color)] px-3 py-2 text-left text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
                          >
                            Tiếng Việt (Vietnamese)
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleExportDocx('en')}
                            className="block w-full border-t border-[var(--border-color)] px-3 py-2 text-left text-sm font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)]"
                          >
                            English
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {meetingArtifactsAvailable && isOwner && (
                    <button
                      type="button"
                      disabled={minutesActionsDisabled || isSavingMinutes}
                      onClick={() => void openShareMinutesEmail()}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <EnvelopeIcon className="h-3.5 w-3.5" />
                      {t.share}
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-5 w-64 max-w-full animate-pulse rounded bg-[var(--bg-tertiary)]" />
            <div className="h-3 w-80 max-w-full animate-pulse rounded bg-[var(--bg-tertiary)]" />
          </div>
        )}
      </header>

      {exportDocxError && (
        <p className="shrink-0 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 pb-2 text-xs text-red-500 sm:px-6">
          {exportDocxError}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto bg-[var(--bg-chat)] px-4 py-5 sm:px-6 sm:py-6">
        {/* An abandoned session keeps its row, so the empty Minutes and
            Transcript cards below need an explanation — otherwise they read as
            a failure of this screen rather than of the session (BRAI-413). */}
        {currentMeeting?.completed === false && (
          <div
            role="status"
            className="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10"
          >
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                {t.meetingUnfinishedTitle}
              </p>
              <p className="mt-0.5 text-sm text-amber-700 dark:text-amber-400/90">
                {t.meetingUnfinishedBody}
              </p>
            </div>
          </div>
        )}
        {/* The rail is reading material — playback and the feedback the
            reader already left. While editing it is one more column competing
            with the fields, so the editor takes the full width instead. */}
        <div
          className={
            isEditMode
              ? 'grid gap-5'
              : 'grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px]'
          }
        >
          <div className="space-y-5">
            <DetailCard title={t.meetingInfoSection}>
              {currentMeeting ? (
                <MeetingInfoFields
                  meeting={currentMeeting}
                  title={displayTitle}
                  locale={locale}
                  t={t}
                  onSaveTitle={commitDraftTitle}
                  readOnly={!isEditing('info')}
                />
              ) : (
                <SectionBodySkeleton label={t.loadingMeeting} lines={4} />
              )}
              {isEditing('info') && !editingWholePage && (
                <SectionEditActions
                  saving={isSavingMinutes}
                  // Believed only when no field is mid-edit: the whole of
                  // why is in `isMinutesSaveDisabled`, between BRAI-691 and
                  // BRAI-704.
                  disabled={isMinutesSaveDisabled({
                    saving: isSavingMinutes,
                    rosterBlocking: false,
                    dirty: sectionDiffers('info'),
                    mayHoldUncommittedEdit: editableFieldFocused,
                  })}
                  onSave={() => void handleSaveSection('info')}
                  onCancel={() => cancelSection('info')}
                  t={t}
                />
              )}
            </DetailCard>

            {meetingArtifactsAvailable ? (
              <MinutesInsightsBody
                title={t.meetingMinutes}
                pending={insightsPending}
                error={showMinutesError}
                errorMessage={minutesSectionError ?? t.failedLoadMeetingDetails}
                retryLabel={t.feedbackRetry}
                onRetry={handleMinutesRetry}
                retryBusy={detailRefetching || isGeneratingMinutes}
                retryable={!meetingMinutesHasNoSpeech}
                pendingLabel={t.finalizingMeeting}
              >
                <DetailCard
                  title={t.purposeSection}
                  // Whatever the list already holds. Gated on an empty list,
                  // it left a card with one purpose in it and no way to add a
                  // second: the control under the list is the editor's, and
                  // the editor is what this button opens (BRAI-678 review).
                  action={
                    canEditMinutes && !isEditing('purpose') ? (
                      <AddRowButton
                        label={t.addPurpose}
                        onClick={() =>
                          beginEditWith(
                            ['purpose'],
                            // Appended, not replaced: this control is now
                            // offered over a list that already has rows in
                            // it, and `['']` would be all of them gone.
                            () =>
                              updateDraftMinutes({
                                purpose: [...draftMinutesRef.current.purpose, ''],
                              }),
                            `purpose-${draftMinutesRef.current.purpose.length}`
                          )
                        }
                      />
                    ) : undefined
                  }
                >
                  <EditableStringList
                    items={minutes.purpose}
                    emptyLabel={t.purposeUnavailable}
                    addLabel={t.addPurpose}
                    removeAria={t.removePurposeAria}
                    placeholder={t.purposePlaceholder}
                    onSave={(purpose) => updateDraftMinutes({ purpose })}
                    readOnly={!isEditing('purpose')}
                    maxLength={MINUTES_PURPOSE_ITEM_MAX_LENGTH}
                    showCounter
                    fieldPrefix="purpose"
                  />
                  {isEditing('purpose') && !editingWholePage && (
                    <SectionEditActions
                      saving={isSavingMinutes}
                      // Believed only when no field is mid-edit: the whole of
                      // why is in `isMinutesSaveDisabled`, between BRAI-691 and
                      // BRAI-704.
                      disabled={isMinutesSaveDisabled({
                        saving: isSavingMinutes,
                        rosterBlocking: false,
                        dirty: sectionDiffers('purpose'),
                        mayHoldUncommittedEdit: editableFieldFocused,
                      })}
                      onSave={() => void handleSaveSection('purpose')}
                      onCancel={() => cancelSection('purpose')}
                      t={t}
                    />
                  )}
                </DetailCard>

                <DetailCard
                  title={t.discussionTopicsSection}
                  count={minutes.topics.length}
                  action={
                    canEditMinutes && !isEditing('topics') ? (
                      <AddRowButton
                        label={t.addTopic}
                        onClick={() =>
                          beginEditWith(
                            ['topics'],
                            () =>
                              updateDraftMinutes({
                                topics: [
                                  ...draftMinutesRef.current.topics,
                                  {
                                    title: '',
                                    overview: '',
                                    discussions: [],
                                    conclusions: [],
                                  },
                                ],
                              }),
                            `topic-title-${draftMinutesRef.current.topics.length}`,
                            // An appended topic otherwise lands against the
                            // bottom edge of the viewport with no context.
                            // This request specifically wants the new editor
                            // centred after the card expands (#595 follow-up).
                            'center'
                          )
                        }
                      />
                    ) : undefined
                  }
                >
                  {minutes.topics.length === 0 && (
                    <p className="mb-2 text-sm text-[var(--text-secondary)]">
                      {t.topicsUnavailable}
                    </p>
                  )}
                  {minutes.topics.map((topic, index) => (
                    <EditableTopicBlock
                      key={`topic-${index}`}
                      topic={topic}
                      topicIndex={index}
                      t={t}
                      onChange={(nextTopic) =>
                        updateDraftMinutes({
                          topics: minutes.topics
                            .map((current, currentIndex) =>
                              currentIndex === index ? nextTopic : current
                            )
                            .filter((item) => item.title.trim() || item.overview.trim()),
                        })
                      }
                      onRemove={() =>
                        updateDraftMinutes({
                          topics: minutes.topics.filter(
                            (_, currentIndex) => currentIndex !== index
                          ),
                        })
                      }
                      onRequestAdd={
                        canEditMinutes
                          ? (field) =>
                              beginEditWith(
                                ['topics'],
                                () =>
                                  updateDraftMinutes({
                                    topics: minutes.topics.map((current, currentIndex) =>
                                      currentIndex === index
                                        ? { ...current, [field]: [...current[field], ''] }
                                        : current
                                    ),
                                  }),
                                // Read before `prepare` appends, so the length
                                // is the new row's index. The field prefix is
                                // singular where the key is plural.
                                `${field === 'discussions' ? 'discussion' : 'conclusion'}-${index}-${minutes.topics[index][field].length}`
                              )
                          : undefined
                      }
                      readOnly={!isEditing('topics')}
                    />
                  ))}
                  {isEditing('topics') && (
                    <AddRowButton
                      label={t.addTopic}
                      variant="block"
                      onClick={() =>
                        updateDraftMinutes({
                          topics: [
                            ...minutes.topics,
                            { title: '', overview: '', discussions: [], conclusions: [] },
                          ],
                        })
                      }
                    />
                  )}
                  {isEditing('topics') && !editingWholePage && (
                    <SectionEditActions
                      saving={isSavingMinutes}
                      // Believed only when no field is mid-edit: the whole of
                      // why is in `isMinutesSaveDisabled`, between BRAI-691 and
                      // BRAI-704.
                      disabled={isMinutesSaveDisabled({
                        saving: isSavingMinutes,
                        rosterBlocking: false,
                        dirty: sectionDiffers('topics'),
                        mayHoldUncommittedEdit: editableFieldFocused,
                      })}
                      onSave={() => void handleSaveSection('topics')}
                      onCancel={() => cancelSection('topics')}
                      t={t}
                    />
                  )}
                </DetailCard>

                {/* Always drawn, even for a meeting with no project: the
                    picker that gives it one lives in here (BRAI-656), and a
                    section hidden until a project exists is a section nobody
                    can use to choose one. The actions are drawn with it —
                    they are the meeting's own, not Backlog's, and the card
                    counted them in its header while listing none (BRAI-678).
                    Only the Backlog-shaped parts are gated: the badge and the
                    push up here, and the two per-row columns, which the table
                    drops itself when there is no config behind them. */}
                <DetailCard
                  title={t.nextActionsSection}
                  count={minutes.action_items.length}
                  /* Against the heading rather than above the table. A
                     full-width field for a four-letter code pushed the whole
                     table down the card, and every other thing this section
                     says about its project — the connection, the name, the
                     push — is already up here (BRAI-678 review). */
                  action={
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {/* Only before a push: once there is an outcome, the
                          results panel below says what actually happened, and
                          leaving this up would contradict it. */}
                      {backlog.config && hasBacklogProject ? (
                        // The badge sits beside the button it qualifies; it
                        // renders nothing until a key works.
                        <BacklogConnectedBadge connection={backlogConnection} t={t} />
                      ) : null}
                      {backlog.config && hasBacklogProject ? (
                        <>
                          <button
                            type="button"
                            onClick={() => void backlog.sync()}
                            // Disabled without a key rather than left to fail:
                            // every Backlog call now carries the caller's own
                            // one, so pressing this would only produce a 403.
                            // Nothing left to create is its own reason:
                            // overwrites are written from their own panels,
                            // so a press here would only earn a refusal
                            // from our own route.
                            disabled={
                              backlog.phase !== 'idle' ||
                              isEditMode ||
                              !backlogConnection.connected ||
                              backlog.creatableCount === 0
                            }
                            title={
                              isEditMode
                                ? t.backlogEditModeHint
                                : !backlogConnection.connected
                                  ? t.backlogSyncNeedsConnection
                                  : backlog.creatableCount === 0
                                    ? t.backlogNothingToCreate
                                    : undefined
                            }
                            className="btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                          >
                            <UploadIcon className="h-3.5 w-3.5" />
                            {backlog.phase === 'syncing' ? t.backlogSyncing : t.backlogPush}
                          </button>
                        </>
                      ) : null}
                    </div>
                  }
                >
                  {!showsProjectPicker && !savedProjectKey && (
                    // Whoever cannot act on it: mutations need ownership, so
                    // a control here would be refused. A sentence has no
                    // business in the header, where the controls are.
                    <p className="mb-3 text-xs text-[var(--text-secondary)]">
                      {t.nextActionsNeedsProject}
                    </p>
                  )}

                  <EditableActionItemsTable
                    items={minutes.action_items}
                    t={t}
                    onChange={(action_items) => updateDraftMinutes({ action_items })}
                    readOnly={!isEditingActions || !isOwner}
                    directory={directory}
                    backlog={backlog}
                    backlogConnected={backlogConnection.connected}
                  />
                  {/* Under the table and against its right edge, whatever
                      the list already holds. It used to appear only while the
                      list was empty, so the one place that adds a row
                      disappeared the moment there was a row. In edit mode the
                      table carries its own dashed `+` and a second control
                      would be two ways to do one thing. */}
                  {canEditMinutes && !isEditingActions && (
                    <div className="mt-2 flex justify-end">
                      <AddRowButton
                        variant="link"
                        label={t.addAction}
                        onClick={() =>
                          beginEditWith(
                            ['actions'],
                            () =>
                              updateDraftMinutes({
                                action_items: [
                                  ...draftMinutesRef.current.action_items,
                                  { content: '', assignee: '', deadline: '', done: false },
                                ],
                              }),
                            `action-content-${draftMinutesRef.current.action_items.length}`
                          )
                        }
                      />
                    </div>
                  )}
                  {backlog.config?.dry_run && (
                    <p className="mt-2 text-xs font-medium text-amber-600">
                      {t.backlogDryRunNotice}
                    </p>
                  )}
                  {backlog.error && (
                    <p role="alert" className="mt-2 text-xs text-red-600">
                      {backlog.error}
                    </p>
                  )}
                  {backlog.outcome && (
                    <BacklogSyncSummaryBar
                      tally={tallySync(
                        backlog.rows,
                        backlog.skipped,
                        minutes.action_items.length,
                      )}
                      t={t}
                    />
                  )}
                  {/* Gated on the same config as the Sync button: with no
                      space configured there is nothing for a key to be
                      checked against, so the panel would only ever fail. */}
                  {backlog.config && (
                    <BacklogConnectionPanel connection={backlogConnection} t={t} />
                  )}
                  {isEditing('actions') && !editingWholePage && (
                    <SectionEditActions
                      saving={isSavingMinutes}
                      // Believed only when no field is mid-edit: the whole of
                      // why is in `isMinutesSaveDisabled`, between BRAI-691 and
                      // BRAI-704.
                      disabled={isMinutesSaveDisabled({
                        saving: isSavingMinutes,
                        rosterBlocking: backlogAssigneeRosterBlockingSave,
                        dirty: sectionDiffers('actions'),
                        mayHoldUncommittedEdit: editableFieldFocused,
                      })}
                      onSave={() => void handleSaveSection('actions')}
                      onCancel={() => cancelSection('actions')}
                      t={t}
                    />
                  )}
                </DetailCard>

                {minutesSaveError && (
                  <p className="text-xs font-medium text-red-500">{minutesSaveError}</p>
                )}
              </MinutesInsightsBody>
            ) : (
              <>
                <DetailCard title={t.purposeSection} />
                <DetailCard title={t.discussionTopicsSection} />
                <DetailCard title={t.nextActionsSection} />
              </>
            )}

            {meetingArtifactsAvailable && (
              <DetailCard
                title={t.bilingualTranscript}
                action={
                  currentMeeting && transcriptTurns.length > 0 ? (
                    transcriptIsTruncated || showFullTranscript ? (
                      <button
                        type="button"
                        onClick={() => setShowFullTranscript((open) => !open)}
                        className="text-xs font-semibold text-[var(--accent)]"
                      >
                        {showFullTranscript ? t.showLessTranscript : t.viewFullTranscript}
                      </button>
                    ) : undefined
                  ) : undefined
                }
              >
                {showTranscriptSkeleton ? (
                  <SectionBodySkeleton label={t.saving} lines={3} />
                ) : showTranscriptError ? (
                  <SectionRetryPanel
                    message={transcriptSectionError ?? t.failedLoadMeetingDetails}
                    retryLabel={t.feedbackRetry}
                    onRetry={() => void refetchMeetingDetail('transcript')}
                    busy={detailRefetching}
                  />
                ) : currentMeeting ? (
                  <StoredTranscriptTable
                    rows={transcriptRows}
                    sourceLang={currentMeeting.sourceLang}
                    targetLang={currentMeeting.targetLang}
                    t={t}
                  />
                ) : null}
              </DetailCard>
            )}
          </div>

          {/* Pinned, so scrolling moves the minutes past it rather than
              carrying it off the screen.

              `top-0`, not a positive offset. The gap under the header is
              already there — it is the scroll container's own `py-6` — and
              the offset is measured from inside that padding, so anything
              above 0 is counted twice and drops the rail below the first
              card before a single pixel has been scrolled. Measured in a
              browser on this markup: `top: 1.5rem` put it 24px low at rest,
              `top: 0` lines it up and still parks it 24px clear of the
              header once stuck.

              `self-start` is not decoration either: a grid item stretches to
              the row height by default, which leaves `sticky` nothing to
              slide within and makes it do nothing at all, silently.

              Only from `xl`, because below it the grid is one column and the
              rail sits under the cards, where pinning it would cover them.
              The height cap is a backstop for a rail taller than the window
              — a long feedback comment can do it — and it is deliberately
              on the generous side, so being out by a few pixels only moves
              when its own scrollbar appears. */}
          {!isEditMode && <aside className="space-y-4 xl:sticky xl:top-0 xl:max-h-[calc(100dvh-10rem)] xl:self-start xl:overflow-y-auto">
            <div className="rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3 text-xs font-medium text-blue-700 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] dark:text-[var(--accent)]">
              {t.automaticallySavedToHistory}
            </div>

            {!currentMeeting ? (
              <MeetingAudioSkeleton label={t.saving} />
            ) : currentMeeting.audioUrl ? (
              <MeetingAudioPlayer
                src={currentMeeting.audioUrl}
                durationSeconds={currentMeeting.durationSeconds}
              />
            ) : currentMeeting.audioUploadStatus === 'FAILED' ? (
              <AudioUnavailableNotice />
            ) : audioSectionError ? (
              <SectionRetryPanel
                message={audioSectionError}
                retryLabel={t.feedbackRetry}
                onRetry={() => void retryAudioStatus()}
                className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm"
              />
            ) : showAudioSkeleton ? (
              <MeetingAudioSkeleton label={t.saving} />
            ) : null}

            <section className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                  {t.yourFeedback}
                </p>
                <button
                  type="button"
                  onClick={() => setFeedbackEditOpen(true)}
                  disabled={!currentMeeting || !isOwner}
                  aria-label={hasFeedback ? t.clickToEdit : t.addFeedbackAria}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--text-tertiary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {hasFeedback ? (
                    <EditIcon className="h-3.5 w-3.5" />
                  ) : (
                    <PlusIcon className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              {hasFeedback && currentMeeting ? (
                <>
                  {currentMeeting.feedbackRating && (
                    <p className="mt-2 text-sm text-[var(--text-primary)]">
                      {fmt(t.translationQualityScore, { rating: currentMeeting.feedbackRating })}
                    </p>
                  )}
                  {currentMeeting.feedbackComment && (
                    <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)]">
                      {currentMeeting.feedbackComment}
                    </p>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm text-[var(--text-secondary)]">{t.noFeedbackProvided}</p>
              )}
            </section>

            {currentMeeting?.realtimeContext ? (
              <section className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--text-secondary)]">
                  {t.yourRealtimeContext}
                </p>
                <p className="mt-2 text-sm leading-6 text-[var(--text-secondary)] whitespace-pre-wrap">
                  {currentMeeting.realtimeContext}
                </p>
              </section>
            ) : null}
          </aside>}
        </div>
      </div>

      {isMoveProjectOpen && currentMeeting ? (
        <MoveProjectDialog
          currentProjectKey={savedProjectKey}
          hasMinutes={Boolean(currentMeeting.insights)}
          hasTranscript={currentMeeting.transcript.length > 0}
          error={moveProjectError}
          busy={isMovingProject}
          onCancel={() => {
            setIsMoveProjectOpen(false)
            setMoveProjectError(null)
          }}
          onMove={(projectKey, regenerate) =>
            void moveMeetingProject(projectKey, regenerate)
          }
        />
      ) : null}

      {feedbackEditOpen && currentMeeting ? (
        <MeetingFeedbackModal
          mode={hasFeedback ? 'edit' : 'create'}
          meeting={{
            title: currentMeeting.title,
            sourceLang: currentMeeting.sourceLang,
            targetLang: currentMeeting.targetLang,
            durationMinutes: Math.round(currentMeeting.durationSeconds / 60),
          }}
          initialRating={currentMeeting.feedbackRating ?? undefined}
          initialComment={currentMeeting.feedbackComment ?? ''}
          isSubmitting={isSavingFeedback}
          error={feedbackSaveError}
          onSubmit={(payload) => void saveFeedback(payload)}
          onClose={() => {
            setFeedbackEditOpen(false)
            setFeedbackSaveError(null)
          }}
        />
      ) : null}

      {showShareEmailModal && currentMeeting ? (
        <ShareMinutesEmailModal
          meetingTitle={displayTitle || currentMeeting.title}
          defaultLocale={shareMinutesDefaultLocale(currentMeeting.targetLang)}
          onClose={() => setShowShareEmailModal(false)}
          onSend={(payload) => handleShareMinutesByEmail(payload)}
        />
      ) : null}

      {shareEmailSent ? (
        <ShareMinutesEmailSentModal
          meetingTitle={shareEmailSent.meetingTitle}
          emails={shareEmailSent.emails}
          onClose={() => setShareEmailSent(null)}
        />
      ) : null}
    </>
  )
}

function SectionBodySkeleton({ label, lines }: { label: string; lines: number }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-label={label}>
      <div className="flex items-center gap-2 text-xs font-medium text-[var(--text-secondary)]">
        <span
          className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[var(--border-color)] border-t-[var(--accent)]"
          aria-hidden="true"
        />
        <span>{label}</span>
      </div>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className={[
            'h-4 animate-pulse rounded bg-[var(--bg-tertiary)]',
            index === lines - 1 ? 'w-2/3' : 'w-full',
          ].join(' ')}
        />
      ))}
    </div>
  )
}

/**
 * Shared pending/error/content gate for the insights-backed minutes sections
 * (purpose / topics / actions).
 *
 * Those three are separate cards once they have content, but there is nothing
 * to split before the analysis lands: one card under `title` carries the
 * skeleton and the Retry, rather than three boxes each saying the same thing.
 */
function MinutesInsightsBody({
  title,
  pending,
  error,
  errorMessage,
  retryLabel,
  onRetry,
  retryBusy = false,
  retryable = true,
  pendingLabel,
  children,
}: {
  title: string
  pending: boolean
  error: boolean
  errorMessage: string
  retryLabel: string
  onRetry: () => void
  retryBusy?: boolean
  /**
   * False for a failure a second attempt cannot change — a recording with no
   * speech in it — which leaves the message standing on its own.
   */
  retryable?: boolean
  pendingLabel: string
  children: ReactNode
}) {
  if (pending) {
    return (
      <DetailCard title={title}>
        <SectionBodySkeleton label={pendingLabel} lines={8} />
      </DetailCard>
    )
  }
  if (error) {
    return (
      <DetailCard title={title}>
        <SectionRetryPanel
          message={errorMessage}
          retryLabel={retryLabel}
          onRetry={retryable ? onRetry : undefined}
          busy={retryBusy}
        />
      </DetailCard>
    )
  }
  return <div className="space-y-5">{children}</div>
}

function SectionRetryPanel({
  message,
  retryLabel,
  onRetry,
  busy = false,
  className,
}: {
  message: string
  retryLabel: string
  /** Omitted for a failure retrying cannot change; the panel is then the message alone. */
  onRetry?: () => void
  busy?: boolean
  className?: string
}) {
  return (
    <div className={['space-y-3 text-center', className].filter(Boolean).join(' ')}>
      <p className="text-sm text-red-500">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          disabled={busy}
          className="btn-primary rounded-md px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          {retryLabel}
        </button>
      ) : null}
    </div>
  )
}

/**
 * Shown in place of {@link MeetingAudioPlayer} when the meeting clearly had
 * a recording (durationSeconds > 0) but no audio ever made it to storage —
 * e.g. the background upload failed after retries. Distinguishes "upload
 * failed" from "no audio was ever expected" (a genuinely empty/upcoming
 * meeting), which previously rendered identically (nothing at all).
 */
function AudioUnavailableNotice() {
  const { t } = useUiStrings()
  return (
    <section className="rounded-lg border border-dashed border-red-200 bg-red-50/60 px-4 py-3 text-center dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)]">
      <p className="text-sm font-semibold text-[var(--text-primary)]">{t.audioUnavailableTitle}</p>
      <p className="mt-1 text-xs text-[var(--text-secondary)]">{t.audioUnavailableHint}</p>
    </section>
  )
}

/**
 * Placeholder shown in place of {@link MeetingAudioPlayer} while the captured
 * audio is still being uploaded in the background after end-meeting.
 */
function MeetingAudioSkeleton({ label }: { label: string }) {
  return (
    <section
      className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm"
      aria-busy="true"
      aria-label={label}
    >
      <div className="flex items-center gap-3">
        <span
          className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--border-color)] border-t-[var(--accent)]"
          aria-hidden="true"
        />
        <span className="text-xs font-medium text-[var(--text-secondary)]">{label}</span>
      </div>
      <div className="mt-3 h-2 w-full animate-pulse rounded-full bg-[var(--bg-tertiary)]" />
    </section>
  )
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 18 9 12l6-6" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.9 3.6a2 2 0 0 1 2.8 2.8L9 17.1 5 18l.9-4L16.9 3.6z"
      />
    </svg>
  )
}

function EnvelopeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="m22 8-10 6L2 8" />
    </svg>
  )
}
